import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../../components/Layout';
import { useAlarmPopup } from '../../context/AlarmPopup';
import { useAlarmSocket } from '../../context/AlarmSocket';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { useAudit, fmtAuditTs, AUDIT_CATEGORY_COLOR, AUDIT_SEVERITY_COLOR } from '../../context/AuditContext';
import { exportCSV, csvDateStamp } from '../../utils/exportCSV';
import Pagination, { usePagination } from '../../components/Pagination';
import SiteMap, { MapAlarm } from './SiteMap';
import { useToast } from '../../context/ToastContext';
import { useLicense } from '../../context/LicenseContext';
import { useAuth } from '../../context/AuthContext';

// ─── Sequential job number generator (module-level, session-stable) ──────────
let _soc_seq = 10; // seeds occupy 0001-0007; leave gap; new alarms start at 0011+
const _wsJobMap = new Map<string, string>();      // ArmorIP alarm id → JOB-NNNN
const _protegeJobMap = new Map<string, string>(); // Protege event id → JOB-NNNN
function nextSOCJob(): string {
  _soc_seq++;
  return `JOB-${String(_soc_seq).padStart(4, '0')}`;
}

// ─── Suppression seed data ────────────────────────────────────────────────────

interface SuppressionWindow {
  id: string; site: string; location: string; reason: string;
  start: string; end: string; createdBy: string; active: boolean;
  alarmTypes: string[];
}

const SEED_SUPPRESSIONS: SuppressionWindow[] = [
  { id:'SW-001', site:'Town Hall',    location:'Level 2 — East Wing',   reason:'Electrical maintenance — contractor on site',   start:'07:00', end:'12:00', createdBy:'J. Okafor',    active:true,  alarmTypes:['Motion Detected','Power Failure'] },
  { id:'SW-002', site:'Civic Centre', location:'Car Park B2',            reason:'HVAC servicing — scheduled works',               start:'08:00', end:'16:00', createdBy:'S. Mitchell',  active:true,  alarmTypes:['Motion Detected','Door Forced Open'] },
  { id:'SW-003', site:'Aquatic Centre',location:'Plant Room',            reason:'Pool pump maintenance — false alarms expected',  start:'06:00', end:'10:00', createdBy:'Admin',        active:false, alarmTypes:['Motion Detected'] },
];

// ─── Events seed data ─────────────────────────────────────────────────────────

interface SysEvent {
  id: number; time: string; type: string; source: string;
  site: string; message: string; severity: 'info'|'warning'|'error';
}

const SEED_EVENTS: SysEvent[] = [
  { id:1,  time:'08:41:22', type:'Alarm',           source:'ArmorIP',      site:'Town Hall',         message:'Duress Button activated — Control Room',            severity:'error' },
  { id:2,  time:'08:39:05', type:'Alarm',           source:'ArmorIP',      site:'Civic Centre',      message:'Door Forced Open — Server Room B2',                  severity:'error' },
  { id:3,  time:'08:35:11', type:'Motion',          source:'Milestone',    site:'Aquatic Centre',    message:'SmartMotion detected — Plant Room, CH4',             severity:'warning' },
  { id:4,  time:'08:21:44', type:'Access',          source:'Protege GX',   site:'Council Depot',     message:'Access denied — unknown card ***3311',               severity:'warning' },
  { id:5,  time:'08:18:10', type:'Health',          source:'System',       site:'All Sites',         message:'Backup heartbeat check — all panels responding',     severity:'info' },
  { id:6,  time:'08:14:00', type:'Alarm',           source:'ArmorIP',      site:'Parramatta Square', message:'Perimeter Breach — North fence line sensor',         severity:'error' },
  { id:7,  time:'07:58:33', type:'Power',           source:'ArmorIP',      site:'River Walk',        message:'Power failure detected — electrical room',            severity:'warning' },
  { id:8,  time:'07:44:22', type:'Access',          source:'Protege GX',   site:'Civic Centre',      message:'Access granted — Wei Zhang, Staff Lift L3',          severity:'info' },
  { id:9,  time:'07:30:01', type:'Integration',     source:'System',       site:'All Sites',         message:'Milestone XProtect — 12 cameras healthy, 0 offline', severity:'info' },
  { id:10, time:'07:18:55', type:'Access',          source:'Protege GX',   site:'Parramatta Square', message:'Access denied — unknown card, Roof Access',          severity:'warning' },
  { id:11, time:'07:00:00', type:'System',          source:'Lemai',        site:'Platform',          message:'Daily system health check passed — all services OK', severity:'info' },
  { id:12, time:'06:45:03', type:'Integration',     source:'System',       site:'All Sites',         message:'Inner Range Integriti offline — check integration settings',severity:'error' },
];

const SITES = ['Town Hall','Civic Centre','Aquatic Centre','Council Depot','Parramatta Square','River Walk'];

const ALARM_TYPES = [
  { type:'Motion Detected',    sev:'medium' },
  { type:'Door Forced Open',   sev:'critical' },
  { type:'Duress Button',      sev:'critical' },
  { type:'Camera Tamper',      sev:'high' },
  { type:'Perimeter Breach',   sev:'high' },
  { type:'Glass Break',        sev:'high' },
  { type:'Fire Panel Input',   sev:'critical' },
  { type:'Power Failure',      sev:'medium' },
  { type:'Loitering',          sev:'low' },
];

interface Alarm {
  id: number;
  type: string;
  site: string;
  location: string;
  time: string;
  severity: string;
  status: 'active'|'acknowledged'|'assigned'|'dispatched'|'closed';
  operator?: string;
  jobNumber?: string;
  assignedTo?: string;
  dispatchedAt?: string;
  resolvedAt?: string;
  tsMs?: number;        // when alarm was raised (epoch ms)
  ackedMs?: number;     // when acknowledged
  closedMs?: number;    // when resolved/closed
  source?: string;      // 'ArmorIP' | 'Protege GX' | 'Demo'
  cameraId?: string;    // camera directly linked by source system (e.g. ArmorIP)
  description?: string;
  repeatCount?: number; // how many times this event repeated (alarm compression)
  lastSeenTs?: string;
  actionLog?: { time: string; op: string; action: string }[];
  sopCompletions?: number[];  // indices of completed SOP steps (persisted)
  sourceId?: string;          // original backend alarm ID (e.g. 'armorip-xxxxx') — used for API calls
}

interface GuardCheckpoint {
  id: string; name: string; location: string; site: string;
  expected: string; completed: boolean; completedAt?: string; overdue?: boolean;
}
interface GuardTour {
  id: string; officer: string; startTime: string; endTime: string;
  status: 'scheduled'|'active'|'completed'|'overdue';
  site: string; completionPct: number;
  checkpoints: GuardCheckpoint[];
}

const SEED_TOURS: GuardTour[] = [
  { id:'T-001', officer:'J. Williams', site:'Town Hall', startTime:'07:00', endTime:'07:45', status:'completed', completionPct:100,
    checkpoints:[
      { id:'CP-001', name:'Main Entry',       location:'Ground Floor - Entry',  site:'Town Hall', expected:'07:05', completed:true, completedAt:'07:04' },
      { id:'CP-002', name:'Carpark B1',        location:'Level B1 - Column A',  site:'Town Hall', expected:'07:12', completed:true, completedAt:'07:11' },
      { id:'CP-003', name:'Server Room',       location:'Level 2 - East Wing',  site:'Town Hall', expected:'07:20', completed:true, completedAt:'07:22' },
      { id:'CP-004', name:'Roof Access',       location:'Level 8',              site:'Town Hall', expected:'07:30', completed:true, completedAt:'07:29' },
      { id:'CP-005', name:'Front Counter',     location:'Ground Floor',         site:'Town Hall', expected:'07:40', completed:true, completedAt:'07:39' },
    ],
  },
  { id:'T-002', officer:'M. Thompson', site:'Civic Centre', startTime:'09:00', endTime:'09:45', status:'active', completionPct:60,
    checkpoints:[
      { id:'CP-006', name:'Main Entry',        location:'Ground Floor',         site:'Civic Centre', expected:'09:05', completed:true, completedAt:'09:04' },
      { id:'CP-007', name:'Level 3 Offices',   location:'East Wing',            site:'Civic Centre', expected:'09:15', completed:true, completedAt:'09:16' },
      { id:'CP-008', name:'Server Room B2',    location:'Basement 2',           site:'Civic Centre', expected:'09:25', completed:true, completedAt:'09:24' },
      { id:'CP-009', name:'Carpark Entry',     location:'B1 Entry Gate',        site:'Civic Centre', expected:'09:35', completed:false },
      { id:'CP-010', name:'Loading Dock',      location:'Rear - North',         site:'Civic Centre', expected:'09:42', completed:false },
    ],
  },
  { id:'T-003', officer:'K. Brown', site:'Council Depot', startTime:'11:00', endTime:'11:45', status:'scheduled', completionPct:0,
    checkpoints:[
      { id:'CP-011', name:'Main Entry',        location:'Ground Floor',         site:'Council Depot', expected:'11:05', completed:false },
      { id:'CP-012', name:'Yard Perimeter',    location:'East Fence Line',      site:'Council Depot', expected:'11:15', completed:false },
      { id:'CP-013', name:'Workshop Area',     location:'Main Shed',            site:'Council Depot', expected:'11:25', completed:false },
      { id:'CP-014', name:'Fuel Store',        location:'West Side',            site:'Council Depot', expected:'11:35', completed:false },
    ],
  },
  { id:'T-004', officer:'A. Singh', site:'Aquatic Centre', startTime:'08:30', endTime:'09:15', status:'overdue', completionPct:25,
    checkpoints:[
      { id:'CP-015', name:'Main Entry',        location:'Entry Foyer',          site:'Aquatic Centre', expected:'08:35', completed:true, completedAt:'08:38' },
      { id:'CP-016', name:'Pool Hall',         location:'Main Pool Deck',       site:'Aquatic Centre', expected:'08:45', completed:false, overdue:true },
      { id:'CP-017', name:'Plant Room',        location:'Basement',             site:'Aquatic Centre', expected:'08:55', completed:false, overdue:true },
      { id:'CP-018', name:'Carpark',           location:'Surface Level',        site:'Aquatic Centre', expected:'09:05', completed:false, overdue:true },
    ],
  },
];

const ONLINE_OPERATORS = ['J. Okafor (SOC)', 'S. Mitchell (SOC)', 'J. Chen (Patrol)'];

// SOP steps per alarm type
const SOP: Record<string, string[]> = {
  'Duress Button': [
    'Review camera feed at alarm location immediately',
    'Attempt contact with person via Jacques intercom',
    'Notify site supervisor — do not wait',
    'Dispatch nearest guard officer to location',
    'If no response within 2 minutes — contact Police (000)',
    'Document all actions, times and observations in job log',
  ],
  'Door Forced Open': [
    'Review all cameras at affected location',
    'Lock down adjacent access points via Protege GX',
    'Contact site supervisor',
    'Dispatch guard — visual check from safe position only',
    'If confirmed intrusion: call Police immediately (000)',
    'Preserve all camera footage — do not overwrite',
    'Document in job log with Police event number if applicable',
  ],
  'Fire Panel Input': [
    'Review cameras at alarm location for visible smoke or fire',
    'If confirmed: call Fire Brigade immediately (000)',
    'Notify site supervisor and building warden',
    'Initiate evacuation protocol for affected site',
    'Guide emergency services via camera and access control system',
    'Do NOT reset fire panel without Fire Brigade clearance',
    'Document all actions and emergency service attendance',
  ],
  'Perimeter Breach': [
    'Review perimeter camera at breach point',
    'Identify person(s) — direction of approach and travel',
    'Lock nearest access points via Protege GX if appropriate',
    'Dispatch guard to intercept — do not engage alone if group',
    'Contact Police if person appears aggressive or armed',
    'Retain 30-second clip reference in Milestone for evidence',
    'Document in job log',
  ],
  'Camera Tamper': [
    'Switch to adjacent cameras to identify tamper location',
    'Check if camera physically damaged, repositioned or obscured',
    'Review last 30 seconds of footage before tamper event',
    'Dispatch guard to inspect camera location',
    'Log fault — raise technician work order if hardware damaged',
    'Document in job log with camera ID and timestamp',
  ],
  'Motion Detected': [
    'Review camera feed at alarm location',
    'Determine if person is authorised for this area and time',
    'If unknown or unauthorised: dispatch guard for visual check',
    'If authorised person: acknowledge and note reason in job log',
    'Escalate to supervisor if unable to identify person on camera',
  ],
  'Glass Break': [
    'Review cameras at alarm location immediately',
    'Assess for break-in or accidental breakage',
    'If break-in confirmed: call Police (000) and dispatch guard',
    'If accidental: contact facilities management for repair',
    'Lock down area if public safety risk',
    'Document in job log',
  ],
  'Power Failure': [
    'Identify which systems are affected (cameras, access control)',
    'Check if UPS backup systems are active',
    'Notify facilities management immediately',
    'Raise work order for power restoration',
    'Monitor for any security gaps caused by power loss',
    'Document in job log with affected equipment list',
  ],
  'Loitering': [
    'Review camera feed and confirm duration of loitering',
    'Attempt to identify individual — note description',
    'If near entry points: dispatch guard to investigate',
    'If individual leaves: log description and direction of travel',
    'If persistent or aggressive: contact Police',
    'Document in job log',
  ],
};

const DEFAULT_SOP = [
  'Review available cameras at alarm location',
  'Assess severity and determine appropriate response',
  'Contact relevant site personnel or supervisor',
  'Dispatch guard if physical response required',
  'Document all observations and actions taken in job log',
];

// ─── Elapsed time hook ────────────────────────────────────────────────────────

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Return epoch ms for an alarm — prefer tsMs field, fall back to parsing time string */
function alarmTsMs(timeStr: string, tsMs?: number): number {
  if (tsMs && tsMs > 0) return tsMs;
  const parts = timeStr.split(':').map(Number);
  if (parts.length < 2 || isNaN(parts[0])) return Date.now();
  const d = new Date();
  d.setHours(parts[0], parts[1] || 0, parts[2] || 0, 0);
  // If the resulting time is in the future (e.g. time string is from yesterday's shift)
  // subtract 24h so elapsed is positive
  if (d.getTime() > Date.now()) d.setDate(d.getDate() - 1);
  return d.getTime();
}

function slaColor(ms: number): string {
  const s = ms / 1000;
  if (s < 300)  return '#16a34a';
  if (s < 600)  return '#f59e0b';
  return '#dc2626';
}

function fmtSla(tsMs: number, nowMs: number): string {
  const elapsed = Math.floor((nowMs - tsMs) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

// ─── Escalation chain ─────────────────────────────────────────────────────────
const ESCALATION_LEVELS = [
  { threshold: 0,    label:'L1 — Operator',    color:'#22c55e', bg:'rgba(34,197,94,0.12)',   next: 5,  nextLabel:'Supervisor' },
  { threshold: 5,    label:'L2 — Supervisor',  color:'#f59e0b', bg:'rgba(245,158,11,0.12)',  next: 15, nextLabel:'Manager' },
  { threshold: 15,   label:'L3 — Manager',     color:'#f97316', bg:'rgba(249,115,22,0.12)',  next: 30, nextLabel:'Director' },
  { threshold: 30,   label:'L4 — Director',    color:'#ef4444', bg:'rgba(239,68,68,0.15)',   next: 60, nextLabel:'Incident Command' },
  { threshold: 60,   label:'L5 — INCIDENT',    color:'#dc2626', bg:'rgba(220,38,38,0.2)',    next: null, nextLabel:null },
];

function getEscalation(elapsedMs: number) {
  const m = elapsedMs / 60000;
  let level = ESCALATION_LEVELS[0];
  for (const l of ESCALATION_LEVELS) {
    if (m >= l.threshold) level = l;
  }
  return level;
}

function msUntilNext(elapsedMs: number, level: typeof ESCALATION_LEVELS[0]): number | null {
  if (!level.next) return null;
  return Math.max(0, level.next * 60000 - elapsedMs);
}

// ─── SOC Widgets definition ───────────────────────────────────────────────────

const SOC_WIDGETS: PageWidget[] = [
  { id:'alarms',      label:'Alarm Feed',    icon:'🚨', description:'Live alarm monitoring and response', defaultVisible:true },
  { id:'map',         label:'Site Maps',     icon:'🗺️', description:'All site maps by controller — view doors, cameras and alarms', defaultVisible:true },
  { id:'tours',       label:'Guard Tours',   icon:'🚶', description:'Patrol scheduling, checkpoint tracking and compliance', defaultVisible:true },
  { id:'sla',         label:'SLA Dashboard', icon:'⏱', description:'Response time metrics and SLA compliance', defaultVisible:true },
  { id:'alarmstatus', label:'Alarm Status',  icon:'🔕', description:'Alarm suppression and status windows', defaultVisible:true },
  { id:'audit',       label:'Audit Trail',   icon:'🔍', description:'Full operator action audit log', defaultVisible:true },
  { id:'events',      label:'System Events', icon:'📡', description:'Aggregated event log from all security systems — ArmorIP, Protege GX, cameras', defaultVisible:true },
];

// ─── Linked cameras per site/location ────────────────────────────────────────
const LINKED_CAMERAS: Record<string, string[]> = {
  'Town Hall':          ['TH-ENTRY-01', 'TH-LOBBY-01', 'TH-ENTRY-02'],
  'Civic Centre':       ['CC-ENTRY-01', 'CC-ATRIUM-01'],
  'Aquatic Centre':     ['AQ-ENTRY-01', 'AQ-POOL-01'],
  'Council Depot':      ['DEP-ENTRY-01'],
  'Parramatta Square':  ['PS-ENTRY-01', 'PS-PLAZA-01'],
  'River Walk':         ['RW-PATH-01'],
};

let idCounter = 100;
function genAlarm(): Alarm {
  const at = ALARM_TYPES[Math.floor(Math.random()*ALARM_TYPES.length)];
  const site = SITES[Math.floor(Math.random()*SITES.length)];
  const locs = ['Entry','Level 1','Level 2','Basement','Rear Gate','Control Room','Server Room','Car Park'];
  const now = new Date();
  return {
    id: ++idCounter, type: at.type, site, location: locs[Math.floor(Math.random()*locs.length)],
    time: `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`,
    severity: at.sev, status:'active',
    jobNumber: nextSOCJob(),
    tsMs: now.getTime(),
  };
}

const SEED_ALARMS: Alarm[] = [
  { id:1, type:'Duress Button',    site:'Town Hall',         location:'Control Room',     time:'08:41:22', severity:'critical', status:'active',       jobNumber:'JOB-0001' },
  { id:2, type:'Door Forced Open', site:'Civic Centre',      location:'Server Room B2',   time:'08:39:05', severity:'critical', status:'active',       jobNumber:'JOB-0002' },
  { id:3, type:'Motion Detected',  site:'Aquatic Centre',    location:'Plant Room',       time:'08:35:11', severity:'warning',   status:'acknowledged', operator:'J. Okafor',  jobNumber:'JOB-0004' },
  { id:4, type:'Camera Tamper',    site:'Council Depot',     location:'Entry Gate',       time:'08:21:44', severity:'high',     status:'acknowledged', operator:'S. Mitchell' },
  { id:5, type:'Perimeter Breach', site:'Parramatta Square', location:'North Perimeter',  time:'08:14:00', severity:'high',     status:'active',       jobNumber:'JOB-0007' },
  { id:6, type:'Power Failure',    site:'River Walk',        location:'Electrical Room',  time:'07:58:33', severity:'warning',   status:'closed', operator:'Admin',      jobNumber:'JOB-0005' },
  { id:7, type:'Loitering',        site:'Town Hall',         location:'Entry Plaza',      time:'07:42:10', severity:'info',      status:'closed', operator:'J. Chen',    jobNumber:'JOB-0006' },
];

const SEV_COLOR: Record<string,string> = { critical:'#ef4444', high:'#f59e0b', medium:'#3b82f6', low:'#64748b' };
const SEV_BG:    Record<string,string> = { critical:'rgba(239,68,68,0.1)', high:'rgba(245,158,11,0.1)', medium:'rgba(59,130,246,0.1)', low:'rgba(100,116,139,0.1)' };

// ── Load stream / VMS config from localStorage ────────────────────────────────
function getCamStreams(): Record<string,string> {
  try { return JSON.parse(localStorage.getItem('lemai_cam_streams') || '{}'); } catch { return {}; }
}
function getVmsUrl(): string {
  return localStorage.getItem('lemai_vms_url') || '';
}

// ─── Camera Feed Player ────────────────────────────────────────────────────────
// Supports: MJPEG/snapshot (img), HLS (video), or iframe (web client)

function ClipPlayer({ camera, onClose }: { camera: string; onClose: () => void }) {
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [imgTs, setImgTs] = useState(Date.now());
  const streamUrl = getCamStreams()[camera] || '';
  const vmsUrl = getVmsUrl();

  const isSnapshot = streamUrl && /\.(jpg|jpeg|png|cgi|snapshot)(\?|$)/i.test(streamUrl);
  const isHls      = streamUrl && /\.m3u8(\?|$)/i.test(streamUrl);
  const isIframe   = streamUrl && !isSnapshot && !isHls;

  // Refresh snapshot every 2 seconds for MJPEG-like effect
  useEffect(() => {
    if (!isSnapshot) return;
    const t = setInterval(() => setImgTs(Date.now()), 2000);
    return () => clearInterval(t);
  }, [isSnapshot]);

  // Simulated progress for non-stream mode
  useEffect(() => {
    if (streamUrl || !playing) return;
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { setPlaying(false); clearInterval(interval); return 100; }
        return p + (100/30/10);
      });
    }, 100);
    return () => clearInterval(interval);
  }, [playing, streamUrl]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.9)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }}>
      <div style={{ background:'#0d1321', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'16px', width:'720px', maxWidth:'95vw' }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontSize:'13px', fontWeight:700, color:'#e2e8f0' }}>
              {streamUrl ? 'Live Camera Feed' : '30-Second Review'} — {camera}
            </div>
            <div style={{ fontSize:'10px', color:'#475569', marginTop:'2px' }}>
              {streamUrl ? (isSnapshot ? 'MJPEG snapshot · refreshes every 2s' : isHls ? 'HLS live stream' : 'Camera web interface') : 'Milestone XProtect · Pre-event clip'}
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            {vmsUrl && (
              <button onClick={() => window.open(vmsUrl, '_blank')}
                style={{ padding:'5px 12px', background:'rgba(59,130,246,0.12)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'7px', color:'#3b82f6', fontSize:'10px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                Open in VMS →
              </button>
            )}
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', color:'#64748b', width:'30px', height:'30px', fontSize:'14px', cursor:'pointer' }}>✕</button>
          </div>
        </div>

        {/* Video area */}
        <div style={{ position:'relative', height:'360px', background:'#000', overflow:'hidden' }}>
          {isSnapshot && (
            <img src={`${streamUrl}${streamUrl.includes('?') ? '&' : '?'}t=${imgTs}`}
              alt={camera}
              style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
              onError={e => { (e.target as HTMLImageElement).style.display='none'; }}
            />
          )}
          {isHls && (
            <video src={streamUrl} autoPlay muted controls style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
          )}
          {isIframe && (
            <iframe src={streamUrl} title={camera} style={{ width:'100%', height:'100%', border:'none', display:'block' }} />
          )}
          {!streamUrl && (
            <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'8px' }}>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.2)', letterSpacing:'2px' }}>MILESTONE XPROTECT</div>
              <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.5)', fontFamily:'monospace' }}>{camera}</div>
              <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)' }}>
                {playing ? `Playing... ${Math.round(progress * 0.3)}s / 30s` : 'Clip ended · open VMS for full playback'}
              </div>
              <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.15)', marginTop:'8px' }}>
                Configure stream URL in Settings → Cameras to show live feed
              </div>
              <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, background:'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.01) 2px, rgba(255,255,255,0.01) 4px)', pointerEvents:'none' }} />
            </div>
          )}
          {/* Live badge */}
          {(streamUrl || playing) && (
            <div style={{ position:'absolute', top:'12px', left:'14px', display:'flex', alignItems:'center', gap:'5px' }}>
              <div style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#ef4444', animation:'pulse 1s infinite' }} />
              <span style={{ fontSize:'10px', color:'#ef4444', fontWeight:700 }}>{streamUrl ? 'LIVE' : 'PLAYBACK'}</span>
            </div>
          )}
          {!streamUrl && (
            <div style={{ position:'absolute', top:'12px', right:'14px', fontSize:'10px', color:'rgba(255,255,255,0.4)', fontFamily:'monospace' }}>
              {new Date().toLocaleDateString('en-AU')} {new Date().toTimeString().slice(0,8)}
            </div>
          )}
          <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
        </div>
        {/* Footer */}
        <div style={{ padding:'12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            {!streamUrl && <span style={{ fontSize:'10px', color:'#475569' }}>{Math.round(progress*0.3)}s / 30s</span>}
            {streamUrl && isSnapshot && <span style={{ fontSize:'10px', color:'#22c55e' }}>● Live snapshot · refreshes every 2s</span>}
            {streamUrl && isHls && <span style={{ fontSize:'10px', color:'#22c55e' }}>● HLS live stream</span>}
            {streamUrl && isIframe && <span style={{ fontSize:'10px', color:'#22c55e' }}>● Camera web interface</span>}
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            {!streamUrl && vmsUrl && (
              <button onClick={() => window.open(vmsUrl, '_blank')}
                style={{ padding:'6px 14px', borderRadius:'7px', border:'none', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                Open in VMS →
              </button>
            )}
            <button onClick={onClose}
              style={{ padding:'6px 14px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#64748b', fontSize:'11px', cursor:'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Alarm Response Panel ─────────────────────────────────────────────────────

function ActionLogSection({ alarm, onAddAction }: { alarm: Alarm; onAddAction?: (id: number, time: string, action: string) => void }) {
  const [logAction, setLogAction] = useState('');
  const entries = alarm.actionLog || [];
  function nowStr() { const d = new Date(); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`; }
  function submit() {
    if (!logAction.trim() || !onAddAction) return;
    onAddAction(alarm.id, nowStr(), logAction.trim());
    setLogAction('');
  }
  return (
    <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
        <div style={{ fontSize:'10px', color:'#475569', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Operator Action Log</div>
        <span style={{ fontSize:'9px', color:'#334155' }}>{entries.length} entr{entries.length===1?'y':'ies'}</span>
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'3px', maxHeight:'130px', overflowY:'auto', marginBottom:'8px' }}>
        {entries.length === 0 ? (
          <div style={{ fontSize:'11px', color:'#334155', fontStyle:'italic', padding:'6px 0' }}>No actions logged yet</div>
        ) : entries.map((entry, i) => (
          <div key={i} style={{ display:'flex', gap:'8px', alignItems:'flex-start', fontSize:'11px', padding:'3px 0', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
            <span style={{ color:'#475569', fontFamily:'monospace', minWidth:'54px', flexShrink:0 }}>{entry.time}</span>
            <span style={{ color:'#3b82f6', fontSize:'9px', maxWidth:'80px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flexShrink:0, paddingTop:'1px' }}>{entry.op}</span>
            <span style={{ color:'#94a3b8', flex:1, lineHeight:'1.3' }}>{entry.action}</span>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:'6px' }}>
        <input value={logAction} onChange={e=>setLogAction(e.target.value)} placeholder="Log an action (e.g. Camera reviewed — no threat visible)"
          style={{ flex:1, padding:'5px 9px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'6px', color:'#e2e8f0', fontSize:'10px', outline:'none' }}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
        />
        <button onClick={submit} disabled={!logAction.trim()}
          style={{ padding:'5px 10px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color: logAction.trim() ? '#3b82f6' : '#334155', fontSize:'10px', fontWeight:600, cursor:logAction.trim()?'pointer':'default', whiteSpace:'nowrap' }}>
          + Log
        </button>
      </div>
    </div>
  );
}

function AlarmResponsePanel({ alarm, onAck, onAssign, onDispatch, onResolve, onClose, onAddAction, onSopToggle, alarmCamConfig, alarmTypesConfig, operatorNames, defaultTab }: {
  alarm: Alarm;
  onAck: (id: number) => void;
  onAssign: (id: number, op: string) => void;
  onDispatch: (id: number) => void;
  onResolve: (id: number, note: string) => void;
  onClose: () => void;
  onAddAction?: (id: number, time: string, action: string) => void;
  onSopToggle?: (alarmId: number, completedIndices: number[]) => void;
  alarmCamConfig: Record<string, string[]>;
  alarmTypesConfig: Record<string, { displayName: string; category: string; sop: string[] }>;
  operatorNames: string[];
  defaultTab?: string;
}) {
  const typeCfg = alarmTypesConfig[alarm.type];
  const steps = (typeCfg?.sop?.length ? typeCfg.sop : null) || SOP[alarm.type] || DEFAULT_SOP;
  const displayName = typeCfg?.displayName || alarm.type;
  const category    = typeCfg?.category;

  // Merge cameras: 1) type-based config (operator-defined), 2) direct camera from source system (e.g. ArmorIP), 3) site-based fallback
  const configCams   = alarmCamConfig[alarm.type] || [];
  const directCam    = alarm.cameraId ? [alarm.cameraId] : [];
  const siteFallback = LINKED_CAMERAS[alarm.site] || [];
  const seen: Record<string,boolean> = {};
  const cameras = [...configCams, ...directCam, ...siteFallback].filter(c => seen[c] ? false : (seen[c] = true));
  const [checked, setChecked] = useState<boolean[]>(() => {
    const base = steps.map(() => false);
    if (alarm.sopCompletions) {
      alarm.sopCompletions.forEach(i => { if (i >= 0 && i < base.length) base[i] = true; });
    }
    return base;
  });
  const [reviewCamera, setReviewCamera] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [selectedOp, setSelectedOp] = useState(alarm.assignedTo || operatorNames[0] || '');
  const [activeTab, setActiveTab] = useState(defaultTab || 'notes');
  useEffect(() => { if (defaultTab) setActiveTab(defaultTab); }, [defaultTab]);

  const toggle = (i: number) => {
    setChecked(prev => {
      const n = prev.slice();
      n[i] = !n[i];
      // Persist to backend
      const newIndices = n.reduce<number[]>((acc, v, idx) => { if (v) acc.push(idx); return acc; }, []);
      if (onSopToggle) onSopToggle(alarm.id, newIndices);
      return n;
    });
  };

  const completedSteps = checked.filter(Boolean).length;
  const pct = steps.length > 0 ? Math.round((completedSteps / steps.length) * 100) : 0;

  const STAGES = ['active','acknowledged','assigned','dispatched','closed'] as const;
  const stageIdx = STAGES.indexOf(alarm.status as any);
  const stageLabels: Record<string,string> = { active:'New', acknowledged:'Acknowledged', assigned:'Assigned', dispatched:'Dispatched', closed:'Resolved' };
  const stageColors: Record<string,string> = { active:'#ef4444', acknowledged:'#f59e0b', assigned:'#3b82f6', dispatched:'#a855f7', closed:'#22c55e' };

  function handleSubmit() {
    setSubmitted(true);
    if (alarm.status === 'active') onAck(alarm.id);
    setTimeout(onClose, 1200);
  }

  return (
    <>
      {reviewCamera && <ClipPlayer camera={reviewCamera} onClose={() => setReviewCamera(null)} />}
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:200, backdropFilter:'blur(3px)' }} onClick={onClose} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:'480px', maxWidth:'95vw', background:'#0d1321', borderLeft:'1px solid rgba(255,255,255,0.08)', zIndex:201, display:'flex', flexDirection:'column', overflowY:'auto' }}>

        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', background:'rgba(0,0,0,0.2)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <div style={{ width:'10px', height:'10px', borderRadius:'50%', background:SEV_COLOR[alarm.severity], boxShadow:`0 0 8px ${SEV_COLOR[alarm.severity]}` }} />
              <div>
                {/* Site badge — at top, before alarm type */}
                {(() => {
                  const SITE_COLORS: Record<string,string> = {
                    'Town Hall':'#f59e0b','Civic Centre':'#3b82f6','Aquatic Centre':'#06b6d4',
                    'Council Depot':'#f97316','Parramatta Square':'#8b5cf6','River Walk':'#22c55e',
                    'All Sites':'#94a3b8','Platform':'#64748b',
                  };
                  const fallbacks = ['#f59e0b','#3b82f6','#06b6d4','#f97316','#8b5cf6','#22c55e','#ec4899','#14b8a6'];
                  const hash = alarm.site.split('').reduce((a:number,c:string)=>a+c.charCodeAt(0),0);
                  const sc = SITE_COLORS[alarm.site] || fallbacks[hash % fallbacks.length];
                  return (
                    <div style={{ marginBottom:'6px' }}>
                      <span style={{ fontSize:'16px', fontWeight:700, color:'#fff', background:`${sc}28`, border:`1.5px solid ${sc}60`, borderRadius:'7px', padding:'3px 12px', display:'inline-flex', alignItems:'center', gap:'5px' }}>
                        <span style={{ fontSize:'12px', lineHeight:1 }}>📍</span> {alarm.site}
                      </span>
                    </div>
                  );
                })()}
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <div style={{ fontSize:'15px', fontWeight:700, color:'#e2e8f0' }}>{displayName}</div>
                  {category && (() => {
                    const catColor: Record<string,string> = { Security:'#ef4444', Technical:'#f59e0b', Fire:'#f97316', Medical:'#22c55e', Access:'#3b82f6', Environmental:'#06b6d4', Other:'#64748b' };
                    return <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'6px', background:`${catColor[category]||'#64748b'}20`, color:catColor[category]||'#64748b', border:`1px solid ${catColor[category]||'#64748b'}40` }}>{category.toUpperCase()}</span>;
                  })()}
                </div>
                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>{alarm.location}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', color:'#64748b', width:'30px', height:'30px', fontSize:'14px', cursor:'pointer', flexShrink:0 }}>✕</button>
          </div>
          {/* Workflow stage chain */}
          <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'10px', overflow:'hidden' }}>
            {STAGES.map((s, i) => {
              const done = i <= stageIdx;
              const active = i === stageIdx;
              const col = active ? stageColors[s] : done ? '#22c55e' : '#334155';
              return (
                <React.Fragment key={s}>
                  <div style={{ padding:'2px 8px', borderRadius:'10px', fontSize:'9px', fontWeight:700, background:done?`${col}18`:'transparent', color:col, border:`1px solid ${done?col+'40':'#334155'}`, flexShrink:0 }}>
                    {done && !active ? '✓ ' : ''}{stageLabels[s]}
                  </div>
                  {i < STAGES.length-1 && <div style={{ width:'12px', height:'1px', background:i<stageIdx?'#22c55e':'#334155', flexShrink:0 }} />}
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' as const }}>
            <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'2px', background:SEV_BG[alarm.severity], color:SEV_COLOR[alarm.severity] }}>{alarm.severity.toUpperCase()}</span>
            <span style={{ fontSize:'10px', color:'#475569', padding:'2px 8px', borderRadius:'6px', background:'rgba(255,255,255,0.04)', fontFamily:'monospace' }}>{alarm.time}</span>
            {alarm.source && (
              <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'6px',
                background: alarm.source === 'ArmorIP' || alarm.source === 'ArmorIP Test' ? 'rgba(168,85,247,0.12)' : 'rgba(59,130,246,0.08)',
                color:      alarm.source === 'ArmorIP' || alarm.source === 'ArmorIP Test' ? '#c084fc' : '#60a5fa',
              }}>{alarm.source}</span>
            )}
            {alarm.jobNumber && (
              <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>
                Job: {alarm.jobNumber}
              </span>
            )}
            <button
              onClick={() => {
                sessionStorage.setItem('lemai_map_focus', JSON.stringify({
                  site: alarm.site,
                  location: alarm.location,
                  alarmType: alarm.type,
                  severity: alarm.severity,
                  time: alarm.time,
                }));
                window.location.href = '/m/access';
              }}
              style={{ fontSize:'10px', fontWeight:700, padding:'2px 10px', borderRadius:'6px', background:'rgba(34,197,94,0.1)', color:'#22c55e', border:'1px solid rgba(34,197,94,0.3)', cursor:'pointer' }}
            >
              📍 View on Map
            </button>
          </div>
          {alarm.description && (
            <div style={{ marginTop:'8px', padding:'6px 10px', background:'rgba(255,255,255,0.03)', borderRadius:'6px', fontSize:'11px', color:'#94a3b8', lineHeight:'1.5', borderLeft:'2px solid rgba(255,255,255,0.1)' }}>
              {alarm.description}
            </div>
          )}
        </div>

        {/* Camera feeds */}
        {(() => {
          const camStreamUrls = getCamStreams();
          const vmsUrl = getVmsUrl();
          const camList = cameras.length > 0 ? cameras : [`${alarm.site} — ${alarm.location}`];
          return (
            <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <div style={{ fontSize:'10px', color:'#475569', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                    Camera Feed{camList.length > 1 ? 's' : ''}
                  </div>
                  <div style={{ fontSize:'9px', color:'#22c55e', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', padding:'1px 6px', borderRadius:'4px', fontWeight:700 }}>
                    {camList.length} linked
                  </div>
                </div>
                <div style={{ display:'flex', gap:'6px' }}>
                  {vmsUrl && (
                    <button onClick={() => window.open(vmsUrl, '_blank')}
                      style={{ padding:'3px 10px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.4)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontSize:'10px', fontWeight:700, cursor:'pointer' }}>
                      Open VMS →
                    </button>
                  )}
                  <button onClick={() => setReviewCamera(camList[0])}
                    style={{ padding:'3px 10px', borderRadius:'6px', border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:'10px', fontWeight:700, cursor:'pointer' }}>
                    ▶ Full Screen
                  </button>
                </div>
              </div>

              {/* Primary camera — large preview */}
              {camList.length > 0 && (() => {
                const primaryCam = camList[0];
                const streamUrl = camStreamUrls[primaryCam] || '';
                const isSnapshot = streamUrl && /\.(jpg|jpeg|png|cgi|snapshot)(\?|$)/i.test(streamUrl);
                const isHls      = streamUrl && /\.m3u8(\?|$)/i.test(streamUrl);
                return (
                  <div onClick={() => setReviewCamera(primaryCam)}
                    style={{ background:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', overflow:'hidden', cursor:'pointer', marginBottom:'8px', position:'relative' }}
                    onMouseOver={e => (e.currentTarget as HTMLElement).style.borderColor='rgba(59,130,246,0.5)'}
                    onMouseOut={e => (e.currentTarget as HTMLElement).style.borderColor='rgba(255,255,255,0.1)'}
                  >
                    <div style={{ height:'140px', position:'relative', background:'#000' }}>
                      {streamUrl && isSnapshot && (
                        <img src={streamUrl} alt={primaryCam} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                          onError={e => { (e.target as HTMLImageElement).style.display='none'; }}
                        />
                      )}
                      {streamUrl && isHls && (
                        <video src={streamUrl} autoPlay muted style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                      )}
                      {streamUrl && !isSnapshot && !isHls && (
                        <iframe src={streamUrl} title={primaryCam} style={{ width:'100%', height:'100%', border:'none', display:'block', pointerEvents:'none' }} />
                      )}
                      {!streamUrl && (
                        <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'4px' }}>
                          <div style={{ fontSize:'28px' }}>📷</div>
                          <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)', fontFamily:'monospace' }}>{primaryCam}</div>
                          <div style={{ fontSize:'9px', color:'rgba(255,255,255,0.2)' }}>Click to open in player</div>
                        </div>
                      )}
                      {/* Live badge */}
                      <div style={{ position:'absolute', top:'8px', left:'10px', display:'flex', alignItems:'center', gap:'4px', background:'rgba(0,0,0,0.6)', padding:'2px 7px', borderRadius:'4px' }}>
                        <div style={{ width:'5px', height:'5px', borderRadius:'50%', background: streamUrl ? '#22c55e' : '#64748b', animation: streamUrl ? 'pulse 1.5s infinite' : 'none' }} />
                        <span style={{ fontSize:'8px', color: streamUrl ? '#22c55e' : '#64748b', fontWeight:700 }}>{streamUrl ? 'LIVE' : 'NO STREAM'}</span>
                      </div>
                      {/* Play overlay */}
                      <div style={{ position:'absolute', bottom:'8px', right:'10px', fontSize:'9px', color:'rgba(59,130,246,0.9)', fontWeight:700, background:'rgba(0,0,0,0.5)', padding:'2px 7px', borderRadius:'4px' }}>
                        ▶ Full Screen
                      </div>
                    </div>
                    <div style={{ padding:'6px 10px', background:'rgba(59,130,246,0.05)', borderTop:'1px solid rgba(255,255,255,0.05)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <span style={{ fontSize:'10px', fontWeight:700, color:'#93c5fd', fontFamily:'monospace' }}>{primaryCam}</span>
                        <span style={{ fontSize:'9px', color:'#475569', marginLeft:'8px' }}>{alarm.site} · {alarm.location}</span>
                      </div>
                      {streamUrl && <span style={{ fontSize:'9px', color:'#22c55e', fontWeight:600 }}>● Live</span>}
                    </div>
                  </div>
                );
              })()}

              {/* Secondary cameras — smaller tiles */}
              {camList.length > 1 && (
                <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(camList.length - 1, 3)},1fr)`, gap:'6px' }}>
                  {camList.slice(1).map(cam => {
                    const streamUrl = camStreamUrls[cam] || '';
                    return (
                      <div key={cam} onClick={() => setReviewCamera(cam)}
                        style={{ background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'8px', overflow:'hidden', cursor:'pointer' }}
                        onMouseOver={e => (e.currentTarget as HTMLElement).style.borderColor='rgba(59,130,246,0.4)'}
                        onMouseOut={e => (e.currentTarget as HTMLElement).style.borderColor='rgba(255,255,255,0.07)'}
                      >
                        <div style={{ height:'60px', display:'flex', alignItems:'center', justifyContent:'center', position:'relative', background:'#000' }}>
                          {streamUrl && /\.(jpg|jpeg|png|cgi)(\?|$)/i.test(streamUrl) ? (
                            <img src={streamUrl} alt={cam} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => { (e.target as HTMLImageElement).style.display='none'; }} />
                          ) : (
                            <div style={{ fontSize:'16px' }}>📷</div>
                          )}
                        </div>
                        <div style={{ padding:'3px 6px', fontSize:'9px', color:'#64748b', fontFamily:'monospace', textAlign:'center', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cam}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Tab bar */}
        <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
          {(['sop','notes'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flex:1, padding:'8px', background:'transparent', border:'none',
              borderBottom: activeTab===tab ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab===tab ? '#e2e8f0' : '#475569',
              fontSize:'10px', fontWeight:700, textTransform:'uppercase' as const, letterSpacing:'0.5px',
              cursor:'pointer',
            }}>
              {tab === 'sop' ? 'SOP Checklist' : 'Notes & Actions'}
            </button>
          ))}
        </div>

        {/* SOP tab */}
        {activeTab === 'sop' && (
          <div style={{ padding:'14px 20px', flex:1 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
              <div style={{ fontSize:'10px', color:'#475569', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>
                Standard Operating Procedure
              </div>
              <div style={{ fontSize:'10px', color: pct===100 ? '#22c55e' : '#64748b' }}>{completedSteps}/{steps.length} steps · {pct}%</div>
            </div>
            {/* Progress bar */}
            <div style={{ background:'rgba(255,255,255,0.06)', borderRadius:'4px', height:'3px', marginBottom:'12px', overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, height:'100%', background: pct===100 ? '#22c55e' : '#3b82f6', transition:'width 0.3s', borderRadius:'4px' }} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {steps.map((step, i) => (
                <div key={i} onClick={() => toggle(i)} style={{
                  display:'flex', gap:'10px', alignItems:'flex-start', padding:'8px 10px', borderRadius:'8px',
                  background: checked[i] ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${checked[i] ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)'}`,
                  cursor:'pointer', transition:'all 0.15s',
                }}>
                  <div style={{
                    width:'16px', height:'16px', borderRadius:'4px', flexShrink:0, marginTop:'1px',
                    background: checked[i] ? '#22c55e' : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${checked[i] ? '#22c55e' : 'rgba(255,255,255,0.15)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'10px', color:'white', transition:'all 0.15s',
                  }}>
                    {checked[i] ? '✓' : ''}
                  </div>
                  <div style={{ fontSize:'11px', color: checked[i] ? '#64748b' : '#94a3b8', lineHeight:'1.5', textDecoration: checked[i] ? 'line-through' : 'none', transition:'all 0.15s' }}>
                    <span style={{ color: checked[i] ? '#475569' : '#475569', fontSize:'10px', fontWeight:600 }}>Step {i+1} </span>
                    {step}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => { onAck(alarm.id); onClose(); }}
              style={{
                width:'100%', height:32,
                backgroundColor:'#16a34a',
                border:'none', color:'#fff',
                fontSize:10, textTransform:'uppercase' as const,
                fontWeight:700, borderRadius:2,
                cursor:'pointer', marginTop:12
              }}
            >
              ✓ ACKNOWLEDGE & SUBMIT
            </button>
            <button
              onClick={() => { onAck(alarm.id); onClose(); }}
              style={{
                width:'100%', height:26,
                backgroundColor:'transparent',
                border:'1px solid #333', color:'#555',
                fontSize:9, textTransform:'uppercase' as const,
                borderRadius:2, cursor:'pointer',
                marginTop:6
              }}
            >
              ACKNOWLEDGE WITHOUT SOP
            </button>
            <button
              onClick={onClose}
              style={{
                width:'100%', height:24,
                backgroundColor:'transparent',
                border:'none', color:'#444',
                fontSize:9, cursor:'pointer',
                marginTop:4
              }}
            >
              KEEP OPEN
            </button>
          </div>
        )}

        {/* Notes tab */}
        {activeTab === 'notes' && (
          <>
            {/* Operator action log */}
            <ActionLogSection alarm={alarm} onAddAction={onAddAction} />

            {/* Operator assignment */}
            {(alarm.status === 'acknowledged' || alarm.status === 'active') && (
              <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(59,130,246,0.03)' }}>
                <div style={{ fontSize:'10px', color:'#475569', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Assign to Operator</div>
                <div style={{ display:'flex', gap:'8px' }}>
                  <select value={selectedOp} onChange={e=>setSelectedOp(e.target.value)}
                    style={{ flex:1, padding:'6px 10px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'#e2e8f0', fontSize:'11px', outline:'none' }}>
                    {operatorNames.map(op=><option key={op} style={{background:'#1e293b'}}>{op}</option>)}
                  </select>
                  <button onClick={()=>{ onAssign(alarm.id, selectedOp); }}
                    style={{ padding:'6px 14px', borderRadius:'7px', border:'1px solid rgba(59,130,246,0.4)', background:'rgba(59,130,246,0.12)', color:'#3b82f6', fontSize:'11px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                    Assign
                  </button>
                </div>
              </div>
            )}
            {alarm.status === 'assigned' && (
              <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(168,85,247,0.03)' }}>
                <div style={{ fontSize:'10px', color:'#a855f7', fontWeight:700, marginBottom:'4px' }}>Assigned to: {alarm.assignedTo}</div>
                <button onClick={()=>onDispatch(alarm.id)}
                  style={{ width:'100%', padding:'8px', borderRadius:'8px', border:'1px solid rgba(168,85,247,0.4)', background:'rgba(168,85,247,0.12)', color:'#c084fc', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
                  Mark as Dispatched — Officer En Route
                </button>
              </div>
            )}
            {alarm.status === 'dispatched' && (
              <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(34,197,94,0.03)' }}>
                <div style={{ fontSize:'10px', color:'#22c55e', fontWeight:700, marginBottom:'4px' }}>Officer dispatched at {alarm.dispatchedAt} · On scene</div>
                <button onClick={()=>onResolve(alarm.id, note)}
                  style={{ width:'100%', padding:'8px', borderRadius:'8px', border:'1px solid rgba(34,197,94,0.4)', background:'rgba(34,197,94,0.12)', color:'#22c55e', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
                  Mark Resolved — Close Job
                </button>
              </div>
            )}
            {/* Notes + submit */}
            <div style={{ padding:'14px 20px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                placeholder="Add notes to job log (observations, actions taken, persons identified)..."
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const, resize:'none' as const, marginBottom:'10px' }} />
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={onClose} style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.08)', background:'transparent', color:'#64748b', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                  Keep Open
                </button>
                <button onClick={handleSubmit} style={{
                  flex:2, padding:'8px', borderRadius:'8px', border:'none', fontSize:'12px', fontWeight:700, cursor:'pointer',
                  background: submitted ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
                  color: submitted ? '#22c55e' : 'white', transition:'all 0.2s',
                }}>
                  {submitted ? '✓ Submitted to Job Log' : alarm.status === 'active' ? 'Acknowledge & Update Job' : 'Update Job'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Main SOC Dashboard ───────────────────────────────────────────────────────

export default function SOCDashboard() {
  const { triggerAlarm } = useAlarmPopup();
  const { alarms: wsAlarms, wsStatus, audioEnabled, setAudioEnabled } = useAlarmSocket();
  const now = useNow(1000);
  const { toast } = useToast();
  const { isDemoOrg, isProtegeOrg } = useLicense();

  const [initialLoading, setInitialLoading] = useState(true);
  const [alarms, setAlarms] = useState<Alarm[]>(() => isDemoOrg ? SEED_ALARMS : []);
  const alarmsRef = useRef<Alarm[]>(alarms);
  useEffect(() => { alarmsRef.current = alarms; }, [alarms]);
  const [mainTab, setMainTab] = useState<'alarms'|'map'|'tours'|'sla'|'alarmstatus'|'audit'|'events'>('alarms');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const [evtFilter, setEvtFilter] = useState<string>('all');
  const [evtSearch, setEvtSearch] = useState('');

  // Alarm-type → camera config — load from backend, fall back to localStorage
  const [alarmCamConfig, setAlarmCamConfig] = useState<Record<string,string[]>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_alarm_type_cameras') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token');
    fetch('/api/integrations/cameras/config', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { if (data.alarm_cam_config) setAlarmCamConfig(data.alarm_cam_config); })
      .catch(() => {
        try {
          const local = localStorage.getItem('lemai_alarm_type_cameras');
          if (local) setAlarmCamConfig(JSON.parse(local));
        } catch {}
      });
  }, []);

  // Fetch operator names from API on mount
  useEffect(() => {
    const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token');
    fetch('/api/users?role=operator', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const users = Array.isArray(data) ? data : data.users || [];
        const names = users.map((u:any) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email);
        if (names.length > 0) setOperatorNames(names);
      })
      .catch(() => {});
  }, []);

  // Live job counts from API (refreshed every 60s)
  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token');
      try {
        const res = await fetch('/api/jobs', { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await res.json();
        const jobs = Array.isArray(data) ? data : data.jobs || [];
        const today = new Date().toDateString();
        setLiveJobCount({
          open: jobs.filter((j:any) => j.status === 'open' || j.status === 'new').length,
          inProgress: jobs.filter((j:any) => j.status === 'in_progress' || j.status === 'assigned').length,
          closedToday: jobs.filter((j:any) => (j.status === 'closed' || j.status === 'complete') && new Date(j.updated_at || j.updatedAt).toDateString() === today).length,
          onHold: jobs.filter((j:any) => j.status === 'on_hold' || j.status === 'pending_review').length,
        });
      } catch { /* silent fail — keep previous count */ }
    };
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  // Alarm type display config (displayName, category, SOP) — loaded from backend
  const [alarmTypesConfig, setAlarmTypesConfig] = useState<Record<string, { displayName: string; category: string; sop: string[] }>>({});
  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    fetch('/api/armorip/alarm-types', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.config) setAlarmTypesConfig(data.config); })
      .catch(() => {});
  }, []);
  const { entries: auditEntries, log: auditLog } = useAudit();
  const [auditCategory, setAuditCategory] = useState<string>('all');
  const [auditSearch, setAuditSearch] = useState('');
  const [filter, setFilter] = useState<'all'|'active'|'acknowledged'|'closed'>('all');
  const [alarmSearch, setAlarmSearch] = useState('');
  const [alarmDateRange, setAlarmDateRange] = useState<'live'|'24h'|'7d'|'30d'>('live');
  const [filterSite, setFilterSite] = useState('');
  const [searchResults, setSearchResults]   = useState<Alarm[] | null>(null);  // null = not in search mode
  const [searchLoading, setSearchLoading]   = useState(false);
  const [newIds, setNewIds] = useState<Set<number>>(new Set());
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [selectedAlarm, setSelectedAlarm] = useState<Alarm | null>(null);
  const [panelDefaultTab, setPanelDefaultTab] = useState<string>('notes');
  const [copiedAlarmId, setCopiedAlarmId] = useState<string|number|null>(null);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  function copyAlarmId(e: React.MouseEvent, id: string|number) {
    e.stopPropagation();
    navigator.clipboard.writeText(String(id)).then(() => {
      setCopiedAlarmId(id);
      setTimeout(() => setCopiedAlarmId(null), 1500);
    }).catch(() => {});
  }
  const [suppressions, setSuppressions] = useState<SuppressionWindow[]>(() => isDemoOrg ? SEED_SUPPRESSIONS : []);
  const [showSuppressionModal, setShowSuppressionModal] = useState(false);

  // Load live suppressions from backend
  useEffect(() => {
    if (isDemoOrg) return;
    const tok = localStorage.getItem('lemai_token') || '';
    fetch('/api/armorip/suppression', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.rules) {
          const mapped = d.rules.map((r: any) => ({
            id: r.id, site: r.site || 'All Sites', location: r.location || 'All Locations',
            reason: r.reason, start: r.createdAt ? new Date(r.createdAt).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit',hour12:false}) : '—',
            end: r.expiresAt ? new Date(r.expiresAt * 1000).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit',hour12:false}) : '—',
            createdBy: r.createdBy, active: r.active,
            alarmTypes: r.alarmType ? [r.alarmType] : ['All Types'],
            _ruleId: r.id,
          }));
          setSuppressions(mapped);
        }
      })
      .catch(() => {});
  }, [isDemoOrg]);

  // Protege live data for SOC
  const [protegeEvents,      setProtegeEvents]      = useState<any[]>([]);
  const [protegeControllers, setProtegeControllers] = useState<{id:string;name:string;online:boolean}[]>([]);
  const [lockdownActive,     setLockdownActive]     = useState(false);
  const [showLockdownModal,  setShowLockdownModal]  = useState(false);
  const [lockdownTarget,     setLockdownTarget]     = useState<{id:string;name:string}|null>(null);
  const [showJobsPopup,      setShowJobsPopup]      = useState(false);
  const [liveJobCount, setLiveJobCount] = useState<{open:number, inProgress:number, closedToday:number, onHold:number} | null>(null);
  const [socMapCtrl,         setSocMapCtrl]         = useState<string|null>(null);
  const [lockdownSearch,     setLockdownSearch]     = useState('');
  const [socMapSearch,       setSocMapSearch]       = useState('');
  const [socEditMapLayout,   setSocEditMapLayout]   = useState(false);
  const [socMapBgImage,      setSocMapBgImage]      = useState<string>(() => localStorage.getItem('siteMapBgImage') || '');
  const [socDoorPositions,   setSocDoorPositions]   = useState<Record<string,{x:number;y:number}>>(() => { try { return JSON.parse(localStorage.getItem('siteMapDoorPositions') || '{}'); } catch { return {}; } });
  const [socCameraPositions, setSocCameraPositions] = useState<Record<string,{x:number;y:number}>>(() => { try { return JSON.parse(localStorage.getItem('siteMapCameraPositions') || '{}'); } catch { return {}; } });
  const [socShowZones,       setSocShowZones]       = useState<boolean>(() => localStorage.getItem('siteMapShowZones') !== 'false');
  const socBgUploadRef = useRef<HTMLInputElement>(null);

  // Reload SOC map when selected controller changes (per-controller maps)
  React.useEffect(() => {
    const ctrlSuffix = socMapCtrl ? `_${socMapCtrl}` : '';
    try { setSocDoorPositions(JSON.parse(localStorage.getItem(`siteMapDoorPositions${ctrlSuffix}`) || '{}')); } catch { setSocDoorPositions({}); }
    try { setSocCameraPositions(JSON.parse(localStorage.getItem(`siteMapCameraPositions${ctrlSuffix}`) || '{}')); } catch { setSocCameraPositions({}); }
    setSocMapBgImage(localStorage.getItem(`siteMapBgImage${ctrlSuffix}`) || '');
    setSocShowZones(localStorage.getItem('siteMapShowZones') !== 'false');
    setSocEditMapLayout(false);
  }, [socMapCtrl]);
  const [liveDoorStatus, setLiveDoorStatus] = useState<Record<string,string>>({});
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [handoverNotes, setHandoverNotes] = useState('');
  const [handoverSigned, setHandoverSigned] = useState(false);
  const [handoverIncomingOp, setHandoverIncomingOp] = useState('');
  const [operatorNames, setOperatorNames] = useState<string[]>([]);
  const [guardTours, setGuardTours] = useState<GuardTour[]>(() => isDemoOrg ? SEED_TOURS : []);
  const shiftStartTime = useMemo(() => { const d = new Date(); d.setHours(7,0,0,0); return d; }, []);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [demoMode, setDemoMode] = useState<boolean>(() => localStorage.getItem('lemai_demo_mode') === '1');
  const [bulkClosing, setBulkClosing] = useState(false);
  const [selectedAlarmIds, setSelectedAlarmIds] = useState<Set<number>>(new Set());
  const [bulkActioning, setBulkActioning] = useState(false);
  // Saved filter views
  const [savedViews, setSavedViews] = useState<{name:string;filter:string;search:string}[]>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_alarm_views') || '[]'); } catch { return []; }
  });
  const [showTimeline, setShowTimeline] = useState<Alarm | null>(null);
  const [timelineData, setTimelineData] = useState<any[] | null>(null);
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const { user: authUser } = useAuth();
  const isAdminUser = authUser?.role === 'admin' || authUser?.role === 'super_admin';
  const [slaTarget, setSlaTarget] = useState<number>(() => { const v = localStorage.getItem('socSlaTarget'); return v ? Number(v) : 5; });
  const [editingSlaTarget, setEditingSlaTarget] = useState(false);
  const [slaTargetDraft, setSlaTargetDraft] = useState(5);

  // Anomaly detection polling
  useEffect(() => {
    if (isDemoOrg) return;
    const fetchAnomalies = () => {
      const tok = localStorage.getItem('lemai_token') || '';
      if (!tok) return;
      fetch('/api/armorip/anomalies', { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.anomalies) setAnomalies(d.anomalies); })
        .catch(() => {});
    };
    fetchAnomalies();
    const t = setInterval(fetchAnomalies, 5 * 60 * 1000); // poll every 5 min
    return () => clearInterval(t);
  }, [isDemoOrg]);

  // Track escalation notifications already fired: key = `${alarmId}-L${levelThreshold}`
  const escalationFiredRef = useRef<Set<string>>(new Set());

  const { editing, setEditing, slots, isVisible, orderedWidgets, toggle, moveUp, moveDown, reset } = usePageCustomizer('soc', SOC_WIDGETS);

  useEffect(() => {
    const t = setTimeout(() => setInitialLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  // ── Escalation notifications: fire toast when alarm crosses threshold ──────
  useEffect(() => {
    const activeAlarms = alarms.filter(a => a.status === 'active');
    const nowMs = Date.now();
    for (const alarm of activeAlarms) {
      const elMs = Math.max(0, nowMs - alarmTsMs(alarm.time, alarm.tsMs));
      const esc = getEscalation(elMs);
      const key = `${alarm.id}-L${esc.threshold}`;
      if (!escalationFiredRef.current.has(key)) {
        escalationFiredRef.current.add(key);
        // Escalation level tracked silently — visible on the alarm row escalation bar
      }
    }
  }, [now, alarms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync seed data when org switches
  useEffect(() => {
    setAlarms(isDemoOrg ? SEED_ALARMS : []);
    setSuppressions(isDemoOrg ? SEED_SUPPRESSIONS : []);
    setGuardTours(isDemoOrg ? SEED_TOURS : []);
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load guard tours from backend for real orgs
  useEffect(() => {
    if (isDemoOrg) return;
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    fetch('/api/tours', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then((data: GuardTour[] | null) => { if (data && data.length > 0) setGuardTours(data); })
      .catch(() => {});
  }, [isDemoOrg]);

  // Merge WS alarms into local state — handles both Protege GX and ArmorIP sources.
  // Adds new alarms AND updates existing ones (e.g. acknowledged/closed state changes).
  useEffect(() => {
    if (wsAlarms.length === 0) return;
    setAlarms(prev => {
      const existingByJobNum = new Map(
        prev.filter(a => a.jobNumber).map(a => [a.jobNumber!, a])
      );
      const newAlarms: Alarm[] = [];
      let anyUpdated = false;

      wsAlarms.forEach((wa, i) => {
        const _wsKey = String(wa.id);
        const jobNum = _wsJobMap.get(_wsKey) || (() => { const n = nextSOCJob(); _wsJobMap.set(_wsKey, n); return n; })();
        const alarmStatus: Alarm['status'] = wa.closedAt ? 'closed' : wa.acknowledged ? 'acknowledged' : 'active';
        if (existingByJobNum.has(jobNum)) {
          // Update existing — sync acknowledged/closed state from live source
          const existing = existingByJobNum.get(jobNum)!;
          const updated: Alarm = {
            ...existing,
            status:   alarmStatus,
            operator: wa.acknowledgedBy || existing.operator,
          };
          if (updated.status !== existing.status || updated.operator !== existing.operator) {
            existingByJobNum.set(jobNum, updated);
            anyUpdated = true;
          }
        } else {
          newAlarms.push({
            id:          9000 + Date.now() + i,
            sourceId:    wa.id,   // preserve original backend ID for API calls
            type:        wa.type,
            site:        wa.site,
            location:    wa.location,
            time:        wa.timestamp,
            severity:    wa.severity,
            status:      alarmStatus,
            operator:    wa.acknowledgedBy,
            source:      (wa as any).source || 'ArmorIP',
            cameraId:    (wa as any).cameraId,
            description: (wa as any).description,
            jobNumber:   jobNum,
            tsMs:        (wa as any).tsMs || Date.now(),
            repeatCount: (wa as any).repeatCount,
            lastSeenTs:  (wa as any).lastSeenTs,
          });
        }
      });

      if (newAlarms.length === 0 && !anyUpdated) return prev;

      // Rebuild: new alarms first, then all prev with any updates applied
      const updatedPrev = prev.map(a =>
        a.jobNumber && existingByJobNum.has(a.jobNumber)
          ? existingByJobNum.get(a.jobNumber)!
          : a
      );
      return [...newAlarms, ...updatedPrev].slice(0, 500);
    });
    setLastRefresh(new Date());
  }, [wsAlarms]);

  // Fetch Protege live data for SOC integration
  useEffect(() => {
    if (!isProtegeOrg) return;
    const tok = localStorage.getItem('lemai_token') || '';
    const hdr = { Authorization: `Bearer ${tok}` };
    fetch('/api/protege/events?count=100', { headers: hdr }).then(r=>r.ok?r.json():null).then(d=>{
      if(d?.events) {
        setProtegeEvents(d.events);
        // Map initial Protege events to alarms for Protege orgs
        if (isProtegeOrg) {
          const mapped: Alarm[] = [];
          (d.events || []).forEach((e: any) => {
            const et = ((e.eventType || '') + ' ' + (e.eventDesc || '')).toLowerCase();

            let alarmType: string | null = null;
            let sev = 'warning';
            if (et.includes('duress') || et.includes('panic')) { alarmType = 'Duress Button'; sev = 'critical'; }
            else if (et.includes('forced') || et.includes('force open')) { alarmType = 'Door Forced Open'; sev = 'critical'; }
            else if (et.includes('tamper')) { alarmType = 'Camera Tamper'; sev = 'high'; }
            else if (et.includes('intrusion') || et.includes('perimeter')) { alarmType = 'Perimeter Breach'; sev = 'high'; }
            else if (et.includes('fire') || et.includes('smoke')) { alarmType = 'Fire Panel Input'; sev = 'critical'; }
            else if (et.includes('access denied') || et.includes('deny')) { alarmType = 'Access Denied'; sev = 'warning'; }
            else if (et.includes('door held open') || et.includes('held open')) { alarmType = 'Door Held Open'; sev = 'warning'; }
            if (!alarmType) return;
            const _pk1 = String(e.id || Date.now());
            const jobNum = _protegeJobMap.get(_pk1) || (() => { const n = nextSOCJob(); _protegeJobMap.set(_pk1, n); return n; })();
            mapped.push({
              id: 50000 + Number(e.id || 0),
              type: alarmType, site: e.controller || e.deviceName || 'Protege GX',
              location: e.deviceName || 'Unknown', time: e.timestamp?.slice(11,19) || '--:--',
              severity: sev, status: 'active', jobNumber: jobNum, tsMs: Date.now(), source: 'Protege GX',
            });
          });
          if (mapped.length > 0) setAlarms(prev => {
            const existing = new Set(prev.map(a => a.jobNumber));
            const newOnes = mapped.filter(a => !existing.has(a.jobNumber));
            return [...newOnes, ...prev].slice(0, 50);
          });
        }
      }
    });
    fetch('/api/protege/controllers', { headers: hdr }).then(r=>r.ok?r.json():null).then(d=>{ if(d?.controllers) setProtegeControllers(d.controllers); });
    // Initial door status
    fetch('/api/protege/doors/status', { headers: hdr }).then(r=>r.ok?r.json():null).then(d=>{ if(d?.statuses) setLiveDoorStatus(d.statuses); });
    // Poll door statuses every 20s
    const doorPoll = setInterval(() => {
      fetch('/api/protege/doors/status', { headers: hdr }).then(r=>r.ok?r.json():null).then(d=>{ if(d?.statuses) setLiveDoorStatus(d.statuses); });
    }, 20000);
    const poll = setInterval(() => {
      fetch('/api/protege/events/live', { headers: hdr }).then(r=>r.ok?r.json():null).then(d=>{
        if (!d?.events?.length) return;
        setProtegeEvents(d.events);
        // Auto-inject events into alarm feed
        d.events.forEach((e: any) => {
          const et = ((e.eventType || '') + ' ' + (e.eventDesc || '')).toLowerCase();
          const name = e.deviceName || e.userName || 'Unknown';

          let alarmType: string | null = null;
          let sev: string = 'warning';

          if (et.includes('duress') || et.includes('panic')) { alarmType = 'Duress Button'; sev = 'critical'; }
          else if (et.includes('forced') || et.includes('force open')) { alarmType = 'Door Forced Open'; sev = 'critical'; }
          else if (et.includes('tamper')) { alarmType = 'Camera Tamper'; sev = 'high'; }
          else if (et.includes('intrusion') || et.includes('perimeter')) { alarmType = 'Perimeter Breach'; sev = 'high'; }
          else if (et.includes('fire') || et.includes('smoke')) { alarmType = 'Fire Panel Input'; sev = 'critical'; }
          else if (et.includes('access denied') || et.includes('deny')) { alarmType = 'Access Denied'; sev = 'warning'; }
          else if (et.includes('door held open') || et.includes('held open')) { alarmType = 'Door Held Open'; sev = 'warning'; }

          if (!alarmType) return;

          const _pk2 = String(e.id || Date.now());
          const jobNum = _protegeJobMap.get(_pk2) || (() => { const n = nextSOCJob(); _protegeJobMap.set(_pk2, n); return n; })();
          const na: Alarm = {
            id: 50000 + Number(e.id || 0) + Math.floor(Math.random()*1000),
            type: alarmType,
            site: e.controller || e.deviceName || 'Protege GX',
            location: e.deviceName || 'Unknown location',
            time: e.timestamp?.slice(11,19) || new Date().toTimeString().slice(0,8),
            severity: sev,
            status: 'active',
            jobNumber: jobNum,
            tsMs: Date.now(),
            source: 'Protege GX',
          };
          setAlarms(prev => {
            if (prev.some(a => a.jobNumber === jobNum)) return prev;
            if (sev === 'critical' || sev === 'high') {
              triggerAlarm({ id:`protege-${na.id}`, type:na.type, title:na.type, site:na.site, location:na.location, severity:sev as any, displayMode: sev === 'critical' ? 'popup' : 'banner', cameraLabel:'', timestamp:new Date(), sop: SOP[na.type] || DEFAULT_SOP });
            }
            return [na, ...prev.slice(0,49)];
          });
        });
      });
    }, 10000);
    return () => { clearInterval(poll); clearInterval(doorPoll); };
  }, [isProtegeOrg]);

  // SLA metrics — calculated from real timestamps where available
  const slaMetrics = useMemo(() => {
    const ackedAlarms = alarms.filter(a => a.ackedMs && a.tsMs);
    const closedAlarms = alarms.filter(a => a.closedMs && a.tsMs);
    const mttaValues = ackedAlarms.map(a => (a.ackedMs! - a.tsMs!) / 60000);
    const mttrValues = closedAlarms.map(a => (a.closedMs! - a.tsMs!) / 60000);
    const avgMTTA = mttaValues.length > 0
      ? Math.round((mttaValues.reduce((s,v) => s+v, 0) / mttaValues.length) * 10) / 10
      : null;
    const avgMTTR = mttrValues.length > 0
      ? Math.round((mttrValues.reduce((s,v) => s+v, 0) / mttrValues.length) * 10) / 10
      : null;
    const slaCompliant = alarms.filter(a => (a.severity === 'critical' || a.severity === 'high') && a.ackedMs && a.tsMs)
      .filter(a => (a.ackedMs! - a.tsMs!) / 60000 <= slaTarget).length;
    const slaTotal = alarms.filter(a => a.severity === 'critical' || a.severity === 'high').length;
    return { avgMTTA, avgMTTR, slaTarget, slaCompliant, slaTotal, acked: alarms.filter(a => a.status !== 'active').length };
  }, [alarms, slaTarget]);



  // ── Backend alarm state persistence helpers ──────────────────────────────
  function _persistAlarmState(id: number, endpoint: string, body?: object) {
    // Resolve the backend string ID: prefer sourceId (original armorip/protege ID),
    // fall back to the numeric id. Demo seed alarms (id < 1000) are skipped.
    const alarm = alarms.find(a => a.id === id);
    const backendId = alarm?.sourceId || String(id);
    if (!alarm?.sourceId && id < 1000) return; // skip demo seed alarms
    const tok = localStorage.getItem('lemai_token') || '';
    fetch(`/api/armorip/alarms/${backendId}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: body ? JSON.stringify(body) : undefined,
    }).catch(() => {});
  }

  const ack = useCallback((id: number) => {
    const nowMs = Date.now();
    setAlarms(prev => {
      const alarm = prev.find(a => a.id === id);
      if (alarm) {
        const jobNum = alarm.jobNumber || nextSOCJob();
        const t = new Date().toTimeString().slice(0,8);
        const newJob = {
          id: String(nowMs), number: jobNum,
          type: alarm.type.includes('Duress') ? 'Duress' : alarm.type.includes('Forced') ? 'Break In / Intrusion' : alarm.type.includes('Breach') ? 'Perimeter Breach' : alarm.type.includes('Fire') ? 'Fire Alarm' : 'Other',
          priority: alarm.severity === 'critical' ? 'Critical' : alarm.severity === 'high' ? 'High' : 'Medium',
          site: alarm.site, location: alarm.location, status: 'new',
          createdBy: 'System (Alarm Auto)', createdAt: t, updatedAt: t,
          description: `${alarm.type} at ${alarm.location}. Auto-created from SOC alarm ${id}.`,
          linkedAlarmId: id,
          activity: [{ time: t, operator: 'System', action: 'Job auto-created from alarm', note: `Alarm #${id} — ${alarm.type}, ${alarm.site} ${alarm.location}` }],
        };
        // Post to backend API; falls back silently on error
        const token = localStorage.getItem('lemai_token') || '';
        fetch('/api/jobs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(newJob),
        }).catch(() => {});
      }
      const alarm2 = prev.find(a => a.id === id);
      if (alarm2) auditLog({ category:'alarm', action:'Alarm acknowledged', operator: authUser?.email || 'Operator', site: alarm2.site, target:`${alarm2.type} @ ${alarm2.location}`, severity:'warning' });
      const nowStr = new Date().toTimeString().slice(0,8);
      const opName = authUser?.email || 'Operator';
      return prev.map(a => a.id===id ? { ...a, status:'acknowledged', operator: opName, ackedMs: nowMs, tsMs: a.tsMs ?? nowMs - 60000, actionLog: [...(a.actionLog||[]), { time: nowStr, op: opName, action: 'Alarm acknowledged — job auto-created' }] } : a);
    });
    _persistAlarmState(id, 'acknowledge', { operator: authUser?.email || 'Operator', at: new Date().toTimeString().slice(0,8) });
    toast('Alarm acknowledged — job auto-created in Jobs module', 'success');
  }, [auditLog, toast, authUser]);

  const close = useCallback((id: number) => {
    const now = Date.now();
    const opEmail = authUser?.email || 'Operator';
    setAlarms(prev => {
      const alarm2 = prev.find(a => a.id === id);
      if (alarm2) auditLog({ category:'alarm', action:'Alarm closed', operator: opEmail, site: alarm2.site, target:`${alarm2.type} @ ${alarm2.location}`, severity:'info' });
      return prev.map(a => a.id===id ? { ...a, status:'closed', operator: opEmail, closedMs: now, tsMs: a.tsMs ?? now - 300000 } : a);
    });
    _persistAlarmState(id, 'close', { operator: opEmail, closedAt: new Date().toISOString() });
    toast('Alarm closed and resolved', 'success');
  }, [auditLog, toast, authUser]);

  const assignAlarm = useCallback((id: number, op: string) => {
    const now = new Date().toTimeString().slice(0,8);
    const opEmail = authUser?.email || 'Current User';
    setAlarms(prev => prev.map(a => a.id===id ? { ...a, status:'assigned', assignedTo:op, operator:op, actionLog: [...(a.actionLog||[]), { time: now, op: opEmail, action: `Assigned to ${op}` }] } : a));
    auditLog({ category:'alarm', action:'ASSIGN', operator: authUser?.email || 'Operator', detail:`Alarm ${id} assigned to ${op}`, severity:'info' });
    toast(`Assigned to ${op}`, 'success');
  }, [auditLog, toast, authUser]);

  const dispatchAlarm = useCallback((id: number) => {
    const now = new Date().toTimeString().slice(0,8);
    const opEmail = authUser?.email || 'Current User';
    setAlarms(prev => prev.map(a => a.id===id ? { ...a, status:'dispatched', dispatchedAt:now, actionLog: [...(a.actionLog||[]), { time: now, op: opEmail, action: 'Officer dispatched — en route' }] } : a));
    auditLog({ category:'alarm', action:'DISPATCH', operator: authUser?.email || 'Operator', detail:`Alarm ${id} — officer dispatched at ${now}`, severity:'warning' });
    toast('Officer dispatched - en route', 'warning');
  }, [auditLog, toast, authUser]);

  const resolveAlarm = useCallback((id: number, note: string) => {
    const now = new Date().toTimeString().slice(0,8);
    const opEmail = authUser?.email || 'Current User';
    setAlarms(prev => prev.map(a => a.id===id ? { ...a, status:'closed', resolvedAt:now, actionLog: [...(a.actionLog||[]), { time: now, op: opEmail, action: `Resolved${note?' — '+note:''}` }] } : a));
    auditLog({ category:'access', action:'RESOLVE', operator: authUser?.email || 'Operator', detail:`Alarm ${id} resolved at ${now}${note?' — '+note:''}`, severity:'info' });
    _persistAlarmState(id, 'close');
    toast('Alarm resolved and closed', 'success');
  }, [auditLog, toast, authUser]);

  const handleSopToggle = useCallback((alarmId: number, completedIndices: number[]) => {
    // Update local state
    setAlarms(prev => prev.map(a => a.id === alarmId ? { ...a, sopCompletions: completedIndices } : a));
    setSelectedAlarm(prev => prev && prev.id === alarmId ? { ...prev, sopCompletions: completedIndices } : prev);
    // Persist to backend (fire-and-forget)
    const tok = localStorage.getItem('lemai_token') || '';
    const alarm = alarmsRef.current.find(a => a.id === alarmId);
    if (tok && alarm) {
      fetch(`/api/armorip/alarms/${String(alarmId)}/sop-steps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ completedIndices }),
      }).catch(() => {});
    }
  }, []);

  const addAlarmAction = useCallback((id: number, time: string, action: string) => {
    const opEmail = authUser?.email || 'Current User';
    setAlarms(prev => prev.map(a => a.id === id ? { ...a, actionLog: [...(a.actionLog || []), { time, op: opEmail, action }] } : a));
    setSelectedAlarm(prev => prev && prev.id === id ? { ...prev, actionLog: [...(prev.actionLog || []), { time, op: opEmail, action }] } : prev);
    auditLog({ category: 'access', action: 'ACTION_LOG', operator: opEmail, detail: `Alarm ${id}: ${action}`, severity: 'info' });
  }, [authUser, auditLog]);

  const doorControl = useCallback((doorId: string, cmd: 'lock'|'unlock'|'open') => {
    const tok = localStorage.getItem('lemai_token') || '';
    fetch(`/api/protege/doors/${doorId}/control`, {
      method:'POST', headers:{ Authorization:`Bearer ${tok}`, 'Content-Type':'application/json' },
      body: JSON.stringify({ action: cmd }),
    }).then(r=>r.json()).then(d => {
      toast(d.ok ? `Door ${doorId}: ${cmd} sent` : `Door control failed: ${d.error || 'unknown'}`, d.ok ? 'success' : 'error');
      if (d.ok) auditLog({ category:'access', action:'DOOR_CONTROL', operator: authUser?.email || 'Operator', detail:`${cmd.toUpperCase()} door ${doorId}`, severity:'warning' });
    }).catch(() => toast(`Door command sent (check Protege GX)`, 'warning'));
  }, [auditLog, toast]);

  // Run a server-side search when date range is 7d/30d (hits persistent log)
  const runSearch = useCallback((q: string, range: typeof alarmDateRange) => {
    if (range === 'live') { setSearchResults(null); return; }
    const tok = localStorage.getItem('lemai_token') || '';
    const nowMs = Date.now();
    const daysMap = { '24h': 1, '7d': 7, '30d': 30 } as const;
    const fromMs  = nowMs - daysMap[range as keyof typeof daysMap] * 86400 * 1000;
    setSearchLoading(true);
    const params = new URLSearchParams({ from_ms: String(fromMs), to_ms: String(nowMs), limit: '1000' });
    if (q.trim()) params.set('q', q.trim());
    fetch(`/api/armorip/alarms/search?${params}`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.alarms) { setSearchResults(null); return; }
        const mapped: Alarm[] = data.alarms.map((a: any, i: number) => ({
          id: 8000 + i,
          type: a.type || 'Unknown', site: a.site || '', location: a.location || '',
          time: a.timestamp || '', severity: a.severity || 'medium',
          status: a.closedAt ? 'closed' : a.acknowledged ? 'acknowledged' : 'active',
          operator: a.acknowledgedBy, source: a.source || 'ArmorIP',
          cameraId: a.cameraId, description: a.description,
          jobNumber: `JOB-${String(a.id).padStart(4, '0')}`, tsMs: a.tsMs || 0,
          repeatCount: a.repeatCount, lastSeenTs: a.lastSeenTs,
        }));
        setSearchResults(mapped);
      })
      .catch(() => setSearchResults(null))
      .finally(() => setSearchLoading(false));
  }, []);

  // Trigger search whenever date range or query changes (debounced 400 ms)
  useEffect(() => {
    if (alarmDateRange === 'live' && !alarmSearch.trim()) { setSearchResults(null); return; }
    const t = setTimeout(() => runSearch(alarmSearch, alarmDateRange), 400);
    return () => clearTimeout(t);
  }, [alarmSearch, alarmDateRange, runSearch]);

  // Client-side alarm compression: active alarms with identical type+site+location
  // are collapsed into a single row with a ×N repeat badge.
  // This handles cases where the backend sends multiple queue entries for the same
  // sustained event (e.g. a panel sending Power Failure every 10 seconds).
  const compressAlarms = (list: Alarm[]): Alarm[] => {
    const groupMap = new Map<string, Alarm[]>();
    list.forEach(a => {
      if (a.status === 'active') {
        const key = `${a.type}||${a.site}||${a.location}`;
        groupMap.set(key, [...(groupMap.get(key) || []), a]);
      }
    });
    const absorbed = new Set<number>();
    const out: Alarm[] = [];
    list.forEach(a => {
      if (absorbed.has(a.id)) return;
      if (a.status === 'active') {
        const key = `${a.type}||${a.site}||${a.location}`;
        const group = groupMap.get(key) || [a];
        if (group.length > 1) {
          const totalCount = group.reduce((s, g) => s + (g.repeatCount || 1), 0);
          out.push({ ...group[0], repeatCount: totalCount });
          group.forEach(g => absorbed.add(g.id));
          return;
        }
      }
      absorbed.add(a.id);
      out.push(a);
    });
    return out;
  };

  const baseAlarms = searchResults ?? alarms;
  const nowMs = Date.now();
  const twoHoursAgo = nowMs - 2 * 3600 * 1000;
  const filtered = compressAlarms(
    baseAlarms.filter(a => {
      if (demoMode && a.tsMs && a.tsMs < twoHoursAgo) return false;
      if (filter !== 'all' && a.status !== filter) return false;
      if (filterSite && a.site !== filterSite) return false;
      if (alarmDateRange === 'live' && alarmSearch.trim()) {
        const q = alarmSearch.toLowerCase();
        return [a.type, a.site, a.location, a.description || '', a.source || '']
          .join(' ').toLowerCase().includes(q);
      }
      return true;
    })
  ).sort((a, b) => alarmTsMs(b.time, b.tsMs) - alarmTsMs(a.time, a.tsMs));
  const active = alarms.filter(a=>a.status==='active').length;
  const acked  = alarms.filter(a=>a.status==='acknowledged').length;
  const closed = alarms.filter(a=>a.status==='closed').length;
  const [groupedView, setGroupedView] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const alarmPager = usePagination(filtered, 25);

  // Group correlated alarms — cluster by correlationGroupId when groupedView is on
  const groupedAlarms = useMemo(() => {
    if (!groupedView) return null;
    const groups: Record<string, Alarm[]> = {};
    filtered.forEach(a => {
      const key = (a as any).correlationGroupId || a.id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    });
    return Object.values(groups).sort((a, b) => {
      const aTs = Math.max(...a.map(x => alarmTsMs(x.time, x.tsMs)));
      const bTs = Math.max(...b.map(x => alarmTsMs(x.time, x.tsMs)));
      return bTs - aTs;
    });
  }, [groupedView, filtered]);

  const tabStyle = (t: string): React.CSSProperties => ({
    padding:'5px 14px', borderRadius:'6px', fontSize:'11px', fontWeight:600,
    cursor:'pointer', border:'none', background:'none',
    color: filter===t?'#e2e8f0':'#64748b',
    borderBottom: filter===t?'2px solid #3b82f6':'2px solid transparent',
    transition:'all 0.15s',
  });

  function handleLockdownController(ctrl: {id:string;name:string}) {
    if (!window.confirm(`Initiate lockdown for ${ctrl.name}? This will lock all associated doors immediately.`)) return;
    setLockdownTarget(ctrl);
    setLockdownActive(true);
    setShowLockdownModal(false);
    const tok = localStorage.getItem('lemai_token') || '';
    fetch('/api/protege/lockdown', { method:'POST', headers:{ Authorization:`Bearer ${tok}`, 'Content-Type':'application/json' }, body: JSON.stringify({action:'lock', controllerId: ctrl.id}) })
      .then(() => toast(`Lockdown: ${ctrl.name} - all doors locking`, 'error'))
      .catch(() => toast('Lockdown command sent (check Protege GX)', 'warning'));
    auditLog({ category:'access', action:'LOCKDOWN', operator: authUser?.email || 'Operator', site: ctrl.name, detail:`Emergency lockdown: ${ctrl.name} (ID ${ctrl.id})`, severity:'critical' });
  }

  function demoFireAlarm() {
    triggerAlarm({
      id: `demo-fire-${Date.now()}`,
      type: 'Fire Panel Input',
      title: 'Fire Panel Input — Zone 4',
      site: 'Civic Centre',
      location: 'Level 3 — East Wing',
      severity: 'critical',
      displayMode: 'popup',
      cameraLabel: 'Civic Centre — L3 East Wing Corridor',
      timestamp: new Date(),
      sop: SOP['Fire Panel Input'],
    });
  }

  function demoDuressAlarm() {
    triggerAlarm({
      id: `demo-duress-${Date.now()}`,
      type: 'Duress Button',
      title: 'Duress Button Activated',
      site: 'Town Hall',
      location: 'Ground Floor — Reception',
      severity: 'critical',
      displayMode: 'popup',
      cameraLabel: 'Town Hall — Reception Camera',
      timestamp: new Date(),
      sop: SOP['Duress Button'],
    });
  }

  const accessAlerts = protegeEvents.filter(e => {
    const t = ((e.eventType||'')+(e.eventDesc||'')).toLowerCase();
    return t.includes('deny') || t.includes('denied') || t.includes('forced') || t.includes('tamper');
  }).slice(0, 20);
  const controllersOnline = isProtegeOrg ? protegeControllers.filter(c=>c.online).length : (isDemoOrg ? 12 : 0);
  const controllersTotal  = isProtegeOrg ? protegeControllers.length : (isDemoOrg ? 12 : 0);
  // Jobs popup data - read from localStorage (written by Jobs module)
  const socJobs: any[] = (() => { try { return JSON.parse(localStorage.getItem('lemaiJobs') || '[]'); } catch { return []; } })();
  const openJobs = socJobs.filter((j:any) => j.status !== 'closed');
  // Site cards for map tab
  const socSites = isProtegeOrg
    ? protegeControllers.map(c => ({ id: c.id, name: c.name, online: c.online }))
    : isDemoOrg
    ? SITES.map((s,i) => ({ id: String(i+1), name: s, online: true }))
    : [];

  return (
    <Layout title="SOC Dashboard" subtitle="Security Operations Centre — Alarm monitoring & response">

      {editing && (
        <CustomizerPanel widgets={SOC_WIDGETS} slots={slots} toggle={toggle} moveUp={moveUp} moveDown={moveDown} reset={reset} onClose={() => setEditing(false)} />
      )}


      {selectedAlarm && (
        <AlarmResponsePanel alarm={selectedAlarm} onAck={ack} onAssign={assignAlarm} onDispatch={dispatchAlarm} onResolve={resolveAlarm} onClose={() => { setSelectedAlarm(null); setPanelDefaultTab('notes'); }} onAddAction={addAlarmAction} onSopToggle={handleSopToggle} alarmCamConfig={alarmCamConfig} alarmTypesConfig={alarmTypesConfig} operatorNames={operatorNames} defaultTab={panelDefaultTab} />
      )}

      {/* ── Incident Timeline Modal ── */}
      {showTimeline && (
        <>
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:300 }} onClick={() => setShowTimeline(null)} />
          <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'#0d1321', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'16px', width:'520px', maxWidth:'95vw', maxHeight:'80vh', overflowY:'auto', zIndex:301, padding:'24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'16px' }}>
              <div>
                <div style={{ fontSize:'15px', fontWeight:700, color:'#e2e8f0' }}>Incident Timeline</div>
                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>{showTimeline.type} · {showTimeline.site} · {showTimeline.location}</div>
              </div>
              <button onClick={() => setShowTimeline(null)} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', color:'#64748b', width:'30px', height:'30px', cursor:'pointer', fontSize:'14px' }}>✕</button>
            </div>
            {timelineData === null && <div style={{ textAlign:'center', padding:'32px', color:'#475569', fontSize:'12px' }}>Loading timeline…</div>}
            {timelineData !== null && timelineData.length === 0 && <div style={{ textAlign:'center', padding:'32px', color:'#475569', fontSize:'12px' }}>No timeline events found.</div>}
            {timelineData !== null && timelineData.length > 0 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'0' }}>
                {timelineData.map((ev: any, i: number) => {
                  const COLOR_MAP: Record<string,string> = { alarm_received:'#ef4444', acknowledged:'#22c55e', escalated:'#f59e0b', sop_completed:'#3b82f6', job_created:'#a855f7', closed:'#64748b' };
                  const col = COLOR_MAP[ev.type] || '#475569';
                  return (
                    <div key={i} style={{ display:'flex', gap:'12px', paddingBottom:'16px', position:'relative' }}>
                      {i < timelineData.length - 1 && <div style={{ position:'absolute', left:'11px', top:'20px', bottom:0, width:'2px', background:'rgba(255,255,255,0.06)' }} />}
                      <div style={{ width:'24px', height:'24px', borderRadius:'50%', background:`${col}20`, border:`2px solid ${col}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:col }} />
                      </div>
                      <div style={{ flex:1, paddingTop:'3px' }}>
                        <div style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0' }}>{ev.label}</div>
                        {ev.detail && <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>{ev.detail}</div>}
                        <div style={{ display:'flex', gap:'8px', marginTop:'4px' }}>
                          {ev.time && <span style={{ fontSize:'10px', color:'#475569', fontFamily:'monospace' }}>{ev.time}</span>}
                          {ev.actor && <span style={{ fontSize:'10px', color:'#334155' }}>by {ev.actor}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Operations Brief ── */}
      {(() => {
        const critCount  = alarms.filter(a=>a.status==='active'&&a.severity==='critical').length;
        const highCount  = alarms.filter(a=>a.status==='active'&&a.severity==='high').length;
        const medCount   = alarms.filter(a=>a.status==='active'&&a.severity==='medium').length;
        const activeList = alarms.filter(a=>a.status==='active');
        const oldestMs   = activeList.length > 0 ? Math.max(...activeList.map(a=>now-alarmTsMs(a.time,a.tsMs))) : 0;
        const slaBreach  = activeList.filter(a=>now-alarmTsMs(a.time,a.tsMs)>15*60000).length;
        return (
          <div style={{
            display:'flex', alignItems:'center', gap:'16px', padding:'10px 16px', marginBottom:'14px',
            background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'10px',
            fontSize:'11px', flexWrap:'wrap',
          }}>
            <div style={{display:'flex',alignItems:'center',gap:'5px',fontWeight:700}}>
              <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'#22c55e',display:'inline-block',animation:'pulse 1.4s ease-in-out infinite'}}/>
              <span style={{color:'#22c55e'}}>● LIVE</span>
            </div>
            <span style={{color:'rgba(255,255,255,0.1)'}}>|</span>
            {critCount > 0 && <span style={{color:'#ef4444',fontWeight:700}}>🔴 {critCount} CRITICAL</span>}
            {highCount > 0 && <span style={{color:'#f59e0b',fontWeight:700}}>🟡 {highCount} HIGH</span>}
            {medCount  > 0 && <span style={{color:'#3b82f6',fontWeight:600}}>🔵 {medCount} MEDIUM</span>}
            {activeList.length === 0 && <span style={{color:'#22c55e',fontWeight:600}}>✅ All Clear — No active alarms</span>}
            {activeList.length > 0 && <><span style={{color:'rgba(255,255,255,0.1)'}}>|</span><span style={{color:'#64748b'}}>Oldest active: <span style={{color:slaColor(oldestMs),fontWeight:700}}>{fmtElapsed(oldestMs)}</span></span></>}
            {slaBreach > 0 && <><span style={{color:'rgba(255,255,255,0.1)'}}>|</span><span style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.35)',color:'#ef4444',padding:'2px 8px',borderRadius:'6px',fontWeight:700}}>⚠ {slaBreach} SLA BREACH{slaBreach>1?'ES':''}</span></>}
            <span style={{color:'rgba(255,255,255,0.1)'}}>|</span>
            <span style={{color:'#334155',fontSize:'10px'}}>Online: <span style={{color:'#22c55e'}}>{ONLINE_OPERATORS.join(' · ')}</span></span>
            <span style={{marginLeft:'auto',color:'#334155',fontSize:'10px',fontFamily:'monospace'}}>
              {new Date().toLocaleTimeString('en-AU',{timeZone:'Australia/Sydney',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false})} AEST
            </span>
            <button onClick={()=>setShowHandoverModal(true)}
              style={{padding:'2px 10px',borderRadius:'6px',border:'1px solid rgba(255,255,255,0.1)',background:'rgba(255,255,255,0.04)',color:'#64748b',fontSize:'10px',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
              Shift Handover
            </button>
            <button onClick={toggleFullscreen} title={isFullscreen ? 'Exit Kiosk Mode' : 'Kiosk / Fullscreen Mode'}
              style={{padding:'2px 8px',borderRadius:'6px',border:`1px solid ${isFullscreen?'rgba(34,197,94,0.4)':'rgba(255,255,255,0.1)'}`,background:isFullscreen?'rgba(34,197,94,0.15)':'rgba(255,255,255,0.04)',color:isFullscreen?'#22c55e':'#64748b',fontSize:'10px',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
              {isFullscreen ? '⊠ Exit Kiosk' : '⊡ Kiosk'}
            </button>
          </div>
        );
      })()}

      {/* Stats Row — always visible */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'16px' }}>
        <div onClick={()=>setMainTab('map')} style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'12px',paddingTop:'12px',paddingBottom:'12px',paddingLeft:'14px',paddingRight:'14px',cursor:'pointer',transition:'all 0.15s'}} onMouseOver={e=>(e.currentTarget as HTMLElement).style.borderColor='rgba(34,197,94,0.3)'} onMouseOut={e=>(e.currentTarget as HTMLElement).style.borderColor='rgba(255,255,255,0.06)'}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}><span style={{fontSize:'11px',color:'#64748b'}}>Controllers / Sites</span><span style={{fontSize:'16px'}}>🖥️</span></div>
          <div style={{fontSize:'26px',fontWeight:700,color:'#22c55e'}}>{controllersOnline}<span style={{fontSize:'14px',color:'#64748b'}}>/{controllersTotal||socSites.length||'-'}</span></div>
          <div style={{fontSize:'10px',color:'#475569',marginTop:'4px'}}>{controllersOnline === controllersTotal ? 'All online' : `${(controllersTotal||0) - controllersOnline} offline`}</div>
        </div>
        <div onClick={()=>setMainTab('alarms')} style={{background:'rgba(255,255,255,0.03)',border:`1px solid ${active > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.06)'}`,borderRadius:'12px',paddingTop:'12px',paddingBottom:'12px',paddingLeft:'14px',paddingRight:'14px',cursor:'pointer',transition:'all 0.15s'}} onMouseOver={e=>(e.currentTarget as HTMLElement).style.borderColor='rgba(239,68,68,0.3)'} onMouseOut={e=>(e.currentTarget as HTMLElement).style.borderColor=active>0?'rgba(239,68,68,0.25)':'rgba(255,255,255,0.06)'}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}><span style={{fontSize:'11px',color:'#64748b'}}>Active Alarms</span><span style={{fontSize:'16px'}}>🚨</span></div>
          <div style={{fontSize:'26px',fontWeight:700,color:'#ef4444'}}>{active}</div>
          <div style={{fontSize:'10px',color:'#475569',marginTop:'4px'}}>{acked} acknowledged</div>
        </div>
        <div onClick={()=>setShowJobsPopup(true)} style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'12px',paddingTop:'12px',paddingBottom:'12px',paddingLeft:'14px',paddingRight:'14px',cursor:'pointer',transition:'all 0.15s'}} onMouseOver={e=>(e.currentTarget as HTMLElement).style.borderColor='rgba(59,130,246,0.3)'} onMouseOut={e=>(e.currentTarget as HTMLElement).style.borderColor='rgba(255,255,255,0.06)'}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}><span style={{fontSize:'11px',color:'#64748b'}}>Active Jobs</span><span style={{fontSize:'16px'}}>📋</span></div>
          <div style={{fontSize:'26px',fontWeight:700,color:'#3b82f6'}}>{liveJobCount !== null ? (liveJobCount.open + liveJobCount.inProgress) : '--'}</div>
          <div style={{fontSize:'10px',color:'#475569',marginTop:'4px'}}>Click to view all jobs</div>
        </div>
        <div onClick={isProtegeOrg ? ()=>setShowLockdownModal(true) : undefined} style={{background:lockdownActive?'rgba(239,68,68,0.15)':'rgba(255,255,255,0.03)',border:lockdownActive?'1px solid rgba(239,68,68,0.5)':'1px solid rgba(255,255,255,0.06)',borderRadius:'12px',paddingTop:'12px',paddingBottom:'12px',paddingLeft:'14px',paddingRight:'14px',cursor:isProtegeOrg?'pointer':'default',transition:'all 0.15s'}} onMouseOver={e=>{ if(isProtegeOrg)(e.currentTarget as HTMLElement).style.borderColor='rgba(239,68,68,0.4)'; }} onMouseOut={e=>(e.currentTarget as HTMLElement).style.borderColor=lockdownActive?'rgba(239,68,68,0.5)':'rgba(255,255,255,0.06)'}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:'8px'}}><span style={{fontSize:'11px',color:lockdownActive?'#ef4444':'#64748b'}}>{lockdownActive?'LOCKDOWN ACTIVE':'Emergency Lockdown'}</span><span style={{fontSize:'16px'}}>🔒</span></div>
          <div style={{fontSize:'13px',fontWeight:700,color:lockdownActive?'#ef4444':'#94a3b8'}}>{isProtegeOrg ? (lockdownActive?`${lockdownTarget?.name||'All sites'} locked`:'Select site to lock') : 'Protege GX only'}</div>
          <div style={{fontSize:'10px',color:'#475569',marginTop:'4px'}}>Locks all doors at selected site</div>
        </div>
      </div>

      {/* Anomaly Detection Alerts */}
      {anomalies.length > 0 && (
        <div style={{ marginBottom:'14px', background:'rgba(245,158,11,0.04)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'10px', overflow:'hidden' }}>
          <div style={{ padding:'8px 16px', borderBottom:'1px solid rgba(245,158,11,0.1)', display:'flex', alignItems:'center', gap:'8px' }}>
            <span style={{ fontSize:'12px', fontWeight:700, color:'#f59e0b' }}>AI Pattern Detection</span>
            <span style={{ fontSize:'10px', padding:'1px 8px', borderRadius:'10px', background:'rgba(245,158,11,0.15)', color:'#fbbf24', fontWeight:700 }}>{anomalies.length} anomal{anomalies.length === 1 ? 'y' : 'ies'}</span>
            <span style={{ fontSize:'10px', color:'#64748b', marginLeft:'auto' }}>Last 60 min · rule-based</span>
          </div>
          <div style={{ padding:'8px 16px', display:'flex', flexDirection:'column', gap:'6px' }}>
            {anomalies.map((a: any) => (
              <div key={a.id} style={{ display:'flex', gap:'10px', alignItems:'flex-start', padding:'8px 10px', borderRadius:'8px', background:'rgba(245,158,11,0.06)', border:`1px solid ${a.severity === 'critical' ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.2)'}` }}>
                <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: a.severity === 'critical' ? '#ef4444' : '#f59e0b', flexShrink:0, marginTop:'4px' }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'12px', fontWeight:600, color: a.severity === 'critical' ? '#fca5a5' : '#fbbf24' }}>{a.title}</div>
                  <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>{a.detail}</div>
                </div>
                {a.count && <span style={{ fontSize:'11px', fontWeight:700, color:'#f59e0b', flexShrink:0 }}>×{a.count}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Tab Bar */}
      <div style={{ display:'flex', gap:'2px', marginBottom:'16px', background:'rgba(255,255,255,0.02)', padding:'4px', borderRadius:'10px', border:'1px solid rgba(255,255,255,0.06)', alignItems:'center' }}>
        {orderedWidgets.filter(w => isVisible(w.id)).map(w => {
          const t = w.id as 'alarms'|'map'|'tours'|'sla'|'alarmstatus'|'audit'|'events';
          const LABELS: Record<string,string> = { alarms:'Alarms', map:'Site Maps', tours:'Guard Tours', sla:'SLA', alarmstatus:'Alarm Status', audit:'Audit Trail', events:'System Events' };
          const ICONS: Record<string,string> = { alarms:'🚨', map:'🗺️', tours:'🚶', sla:'⏱', alarmstatus:'🔕', audit:'🔍', events:'📡' };
          const isActive = mainTab === t;
          return (
            <button key={t} onClick={() => setMainTab(t)} style={{
              flex:1, padding:'6px 8px', borderRadius:'7px', border:'none', cursor:'pointer',
              fontSize:'11px', fontWeight:600, transition:'all 0.15s',
              background: isActive ? 'rgba(30,58,138,0.8)' : 'transparent',
              color: isActive ? '#93c5fd' : '#475569',
              display:'flex', alignItems:'center', justifyContent:'center', gap:'5px',
            }}>
              <span style={{ fontSize:'12px', lineHeight:1 }}>{ICONS[t]}</span>
              <span>{LABELS[t] || t}</span>
              {t==='alarms' && active > 0 && <span style={{ background:'rgba(239,68,68,0.25)', color:'#ef4444', borderRadius:'8px', padding:'0 5px', fontSize:'9px', fontWeight:800 }}>{active}</span>}
              {t==='tours' && guardTours.filter(x=>x.status==='overdue').length > 0 && <span style={{ background:'rgba(239,68,68,0.2)', color:'#ef4444', borderRadius:'8px', padding:'0 5px', fontSize:'9px' }}>{guardTours.filter(x=>x.status==='overdue').length}</span>}
              {t==='alarmstatus' && suppressions.filter(s=>s.active).length > 0 && <span style={{ background:'rgba(245,158,11,0.2)', color:'#f59e0b', borderRadius:'8px', padding:'0 5px', fontSize:'9px' }}>{suppressions.filter(s=>s.active).length}</span>}
            </button>
          );
        })}
        <CustomizeButton editing={editing} setEditing={setEditing} />
      </div>

      {/* ── Alarm Feed Tab ── */}
      {mainTab === 'alarms' && (
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden' }}>
          {/* ── Header row 1: title / status tabs / utility buttons ── */}
          <div style={{ padding:'12px 16px 0', borderBottom:'none', display:'flex', alignItems:'center', gap:'12px' }}>
            {/* Left: title + live pill */}
            <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
              <h3 style={{ margin:0, fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Alarm Feed</h3>
              <span style={{
                display:'inline-flex', alignItems:'center', gap:'4px',
                fontSize:'9px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em',
                padding:'2px 7px', borderRadius:'10px',
                background: wsStatus==='connected' ? 'rgba(34,197,94,0.12)' : wsStatus==='connecting' ? 'rgba(245,158,11,0.12)' : 'rgba(100,116,139,0.12)',
                color: wsStatus==='connected' ? '#22c55e' : wsStatus==='connecting' ? '#f59e0b' : '#64748b',
                border: `1px solid ${wsStatus==='connected' ? 'rgba(34,197,94,0.25)' : wsStatus==='connecting' ? 'rgba(245,158,11,0.25)' : 'rgba(100,116,139,0.2)'}`,
              }}>
                <span style={{ width:5, height:5, borderRadius:'50%', background:'currentColor', display:'inline-block', animation: wsStatus==='connected' ? 'pulse 2s infinite' : 'none' }} />
                {wsStatus==='connected' ? 'Live' : wsStatus==='connecting' ? 'Connecting…' : 'Polling'}
              </span>
            </div>

            {/* Center: status filter tabs */}
            <div style={{ display:'flex', gap:'2px', flex:1 }}>
              {(['all','active','acknowledged','closed'] as const).map(t => (
                <button key={t} onClick={() => setFilter(t)} style={{
                  padding:'6px 14px', borderRadius:'7px 7px 0 0', border:'none', cursor:'pointer',
                  fontSize:'11px', fontWeight:600, transition:'all 0.15s',
                  background: filter===t ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: filter===t ? '#93c5fd' : '#64748b',
                  borderBottom: filter===t ? '2px solid #3b82f6' : '2px solid transparent',
                }}>
                  {t.charAt(0).toUpperCase()+t.slice(1)}
                  {t==='all' && <span style={{ marginLeft:'5px', background:'rgba(255,255,255,0.08)', borderRadius:'8px', padding:'0 5px', fontSize:'9px' }}>{filtered.length}</span>}
                  {t==='active' && active > 0 && <span style={{ marginLeft:'5px', background:'rgba(239,68,68,0.2)', color:'#ef4444', borderRadius:'8px', padding:'0 5px', fontSize:'9px', fontWeight:700 }}>{active}</span>}
                </button>
              ))}
              {savedViews.length > 0 && savedViews.map(v => (
                <span key={v.name} style={{ display:'flex', alignItems:'center', gap:'0' }}>
                  <button onClick={() => { setFilter(v.filter as any); setAlarmSearch(v.search); toast(`View "${v.name}" applied`, 'info'); }}
                    style={{ padding:'6px 10px', borderRadius:'7px 0 0 0', border:'none', cursor:'pointer', fontSize:'10px', fontWeight:600, background:'transparent', color:'#475569', borderBottom:'2px solid transparent' }}>
                    {v.name}
                  </button>
                  <button onClick={() => { const u = savedViews.filter(x => x.name !== v.name); setSavedViews(u); localStorage.setItem('lemai_alarm_views', JSON.stringify(u)); }}
                    style={{ padding:'4px 4px 6px', borderRadius:'0 7px 0 0', border:'none', cursor:'pointer', fontSize:'9px', background:'transparent', color:'#334155', borderBottom:'2px solid transparent' }}>✕</button>
                </span>
              ))}
            </div>

            {/* Right: utility buttons */}
            <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
              {/* Lockdown */}
              <button
                onClick={() => setShowLockdownModal(true)}
                style={{
                  height: 26, padding: '0 12px',
                  backgroundColor: '#450a0a',
                  border: '1px solid #dc2626',
                  color: '#dc2626',
                  fontSize: 9, textTransform: 'uppercase' as const, fontWeight: 700,
                  borderRadius: 2, cursor: 'pointer', marginRight: 8
                }}
              >
                🔒 LOCKDOWN
              </button>
              {/* Audio */}
              <button onClick={() => setAudioEnabled(!audioEnabled)} title={audioEnabled ? 'Mute alarms' : 'Unmute alarms'}
                style={{ padding:'5px 7px', borderRadius:'7px', border:`1px solid ${audioEnabled ? 'rgba(34,197,94,0.3)' : 'rgba(100,116,139,0.2)'}`, background: audioEnabled ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.03)', color: audioEnabled ? '#22c55e' : '#475569', fontSize:'13px', lineHeight:1, cursor:'pointer' }}>
                {audioEnabled ? '🔊' : '🔇'}
              </button>
              {/* Overflow menu */}
              <div style={{ position:'relative' as const }}>
                <button onClick={() => setShowHeaderMenu(v => !v)}
                  style={{ padding:'5px 8px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.08)', background: showHeaderMenu ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)', color:'#64748b', fontSize:'13px', lineHeight:1, cursor:'pointer' }}>
                  ⋯
                </button>
                {showHeaderMenu && (
                  <div style={{ position:'absolute' as const, right:0, top:'calc(100% + 4px)', zIndex:200, background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'6px', minWidth:'190px', boxShadow:'0 8px 32px rgba(0,0,0,0.5)' }}
                    onMouseLeave={() => setShowHeaderMenu(false)}>
                    <button onClick={() => { setShowHeaderMenu(false); const name = window.prompt('Name this view:'); if (!name?.trim()) return; const nv = { name: name.trim(), filter, search: alarmSearch }; const u = [...savedViews.filter(v => v.name !== name.trim()), nv]; setSavedViews(u); localStorage.setItem('lemai_alarm_views', JSON.stringify(u)); toast(`View "${name.trim()}" saved`, 'success'); }}
                      style={{ display:'block', width:'100%', padding:'7px 12px', borderRadius:'7px', border:'none', background:'transparent', color:'#94a3b8', fontSize:'11px', textAlign:'left' as const, cursor:'pointer' }}>
                      + Save current view
                    </button>
                    <button onClick={() => { setShowHeaderMenu(false); const next = !demoMode; setDemoMode(next); localStorage.setItem('lemai_demo_mode', next ? '1' : '0'); toast(next ? 'Demo Mode ON' : 'Demo Mode OFF', 'info'); }}
                      style={{ display:'block', width:'100%', padding:'7px 12px', borderRadius:'7px', border:'none', background: demoMode ? 'rgba(168,85,247,0.1)' : 'transparent', color: demoMode ? '#c084fc' : '#94a3b8', fontSize:'11px', textAlign:'left' as const, cursor:'pointer' }}>
                      {demoMode ? '🎭 Demo Mode: ON' : '🎭 Demo Mode: OFF'}
                    </button>
                    {isAdminUser && (
                      <button disabled={bulkClosing} onClick={async () => { setShowHeaderMenu(false); if (!window.confirm('Bulk-close all alarms older than 2 hours?')) return; setBulkClosing(true); const tok = localStorage.getItem('lemai_token') || ''; try { const r = await fetch('/api/armorip/alarms/bulk-close', { method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${tok}`}, body: JSON.stringify({older_than_hours:2,reason:'System maintenance'}) }); const d = await r.json(); if (d.ok) { setAlarms(prev => prev.map(a => (a.tsMs && a.tsMs < (Date.now()-7200000) && a.status==='active') ? {...a, status:'closed', closedMs:Date.now()} : a)); toast(`${d.closed} alarms closed`,'success'); } else toast(`Bulk close failed: ${d.error}`,'error'); } catch { toast('Bulk close error','error'); } finally { setBulkClosing(false); } }}
                        style={{ display:'block', width:'100%', padding:'7px 12px', borderRadius:'7px', border:'none', background:'transparent', color:'#f87171', fontSize:'11px', textAlign:'left' as const, cursor: bulkClosing ? 'wait' : 'pointer', opacity: bulkClosing ? 0.6 : 1 }}>
                        {bulkClosing ? 'Closing…' : '✕ Bulk close (>2h old)'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Header row 2: search / filters / bulk actions ── */}
          <div style={{ padding:'8px 16px 10px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' as const }}>
            {/* Search */}
            <div style={{ position:'relative' as const, display:'flex', alignItems:'center' }}>
              <span style={{ position:'absolute' as const, left:'8px', color:'#475569', fontSize:'11px', pointerEvents:'none' }}>🔍</span>
              <input
                value={alarmSearch}
                onChange={e => setAlarmSearch(e.target.value)}
                placeholder="Search alarms…"
                style={{ paddingLeft:'26px', paddingRight: alarmSearch ? '24px' : '8px', paddingTop:'5px', paddingBottom:'5px', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', color:'#e2e8f0', fontSize:'11px', outline:'none', width:'170px' }}
              />
              {alarmSearch && <button onClick={() => { setAlarmSearch(''); setSearchResults(null); }} style={{ position:'absolute' as const, right:'6px', background:'none', border:'none', color:'#475569', cursor:'pointer', fontSize:'13px', lineHeight:1 }}>×</button>}
            </div>
            {searchLoading && <span style={{ fontSize:'10px', color:'#64748b' }}>Searching…</span>}
            {/* Site filter */}
            <select
              value={filterSite}
              onChange={e => setFilterSite(e.target.value)}
              style={{ padding:'5px 8px', background:'rgba(255,255,255,0.04)', border:`1px solid ${filterSite ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius:'7px', color: filterSite ? '#60a5fa' : '#64748b', fontSize:'11px', cursor:'pointer', outline:'none' }}
            >
              <option value=''>All Sites</option>
              {Array.from(new Set(alarms.map(a => a.site).filter(Boolean))).sort().map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {/* Date range */}
            <div style={{ display:'flex', gap:'2px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'7px', padding:'2px' }}>
              {(['live','24h','7d','30d'] as const).map(r => (
                <button key={r} onClick={() => setAlarmDateRange(r)} style={{ padding:'3px 9px', borderRadius:'5px', border:'none', cursor:'pointer', fontSize:'10px', fontWeight:600, background: alarmDateRange===r ? 'rgba(59,130,246,0.25)' : 'none', color: alarmDateRange===r ? '#60a5fa' : '#64748b' }}>
                  {r === 'live' ? 'Live' : r.toUpperCase()}
                </button>
              ))}
            </div>
            {/* Grouped toggle */}
            <button onClick={() => setGroupedView(v => !v)} title="Group correlated alarms into incident clusters"
              style={{ padding:'5px 10px', borderRadius:'7px', border:`1px solid ${groupedView ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.08)'}`, background: groupedView ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)', color: groupedView ? '#60a5fa' : '#64748b', fontSize:'10px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' as const }}>
              ⊞ {groupedView ? 'Grouped' : 'Group'}
            </button>
            {/* Bulk selection actions — appear inline when items are checked */}
            {selectedAlarmIds.size > 0 && (
              <>
                <div style={{ width:'1px', height:'18px', background:'rgba(255,255,255,0.08)', margin:'0 2px' }} />
                <span style={{ fontSize:'11px', color:'#60a5fa', fontWeight:700 }}>{selectedAlarmIds.size} selected</span>
                <button disabled={bulkActioning} onClick={async () => {
                  if (!window.confirm(`Acknowledge ${selectedAlarmIds.size} alarms?`)) return;
                  setBulkActioning(true);
                  const tok = localStorage.getItem('lemai_token') || '';
                  const ids = Array.from(selectedAlarmIds).map(i => String(i));
                  try {
                    await fetch('/api/armorip/alarms/bulk-action', { method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${tok}`}, body: JSON.stringify({ids, action:'acknowledge', operator: authUser?.email||'Operator'}) });
                    setAlarms(prev => prev.map(a => selectedAlarmIds.has(a.id) ? {...a, status:'acknowledged', operator: authUser?.email||'Operator'} : a));
                    setSelectedAlarmIds(new Set());
                    toast(`${ids.length} alarms acknowledged`, 'success');
                  } catch { toast('Bulk action failed', 'error'); }
                  finally { setBulkActioning(false); }
                }} style={{ padding:'4px 10px', borderRadius:'2px', border:'1px solid rgba(34,197,94,0.35)', background:'rgba(34,197,94,0.1)', color:'#22c55e', fontSize:'10px', fontWeight:700, cursor: bulkActioning ? 'wait' : 'pointer' }}>
                  ✓ ACK
                </button>
                <button disabled={bulkActioning} onClick={async () => {
                  if (!window.confirm(`Close ${selectedAlarmIds.size} alarms?`)) return;
                  setBulkActioning(true);
                  const tok = localStorage.getItem('lemai_token') || '';
                  const ids = Array.from(selectedAlarmIds).map(i => String(i));
                  try {
                    await fetch('/api/armorip/alarms/bulk-action', { method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${tok}`}, body: JSON.stringify({ids, action:'close', operator: authUser?.email||'Operator'}) });
                    setAlarms(prev => prev.map(a => selectedAlarmIds.has(a.id) ? {...a, status:'closed', closedMs:Date.now()} : a));
                    setSelectedAlarmIds(new Set());
                    toast(`${ids.length} alarms closed`, 'success');
                  } catch { toast('Bulk close failed', 'error'); }
                  finally { setBulkActioning(false); }
                }} style={{ padding:'4px 10px', borderRadius:'6px', border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#f87171', fontSize:'10px', fontWeight:700, cursor: bulkActioning ? 'wait' : 'pointer' }}>
                  ✕ Close
                </button>
                <button onClick={() => setSelectedAlarmIds(new Set())} style={{ padding:'4px 7px', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.08)', background:'transparent', color:'#475569', fontSize:'10px', cursor:'pointer' }}>Cancel</button>
              </>
            )}
          </div>
          {/* Protege live access alerts strip */}
          {isProtegeOrg && accessAlerts.length > 0 && (
            <div style={{padding:'10px 16px',borderBottom:'1px solid rgba(255,255,255,0.06)',background:'rgba(239,68,68,0.03)'}}>
              <div style={{fontSize:'10px',color:'#f59e0b',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px'}}>Live Protege GX Alerts</div>
              <div style={{display:'flex',flexDirection:'column',gap:'4px',maxHeight:'140px',overflowY:'auto'}}>
                {accessAlerts.map((e,i) => {
                  const isDenied = ((e.eventType||'')+(e.eventDesc||'')).toLowerCase().includes('den');
                  const isForced = ((e.eventType||'')+(e.eventDesc||'')).toLowerCase().includes('forced');
                  const isTamper = ((e.eventType||'')+(e.eventDesc||'')).toLowerCase().includes('tamper');
                  const col = (isForced||isTamper) ? '#ef4444' : isDenied ? '#f59e0b' : '#64748b';
                  return (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:'8px',padding:'5px 8px',borderRadius:'6px',background:'rgba(255,255,255,0.02)',border:`1px solid ${col}22`}}>
                      <div style={{width:5,height:5,borderRadius:'50%',background:col,flexShrink:0}}/>
                      <span style={{fontSize:'10px',color:col,fontWeight:600,minWidth:'60px'}}>{isTamper?'TAMPER':isForced?'FORCED':isDenied?'DENIED':'ALERT'}</span>
                      <span style={{fontSize:'10px',color:'#94a3b8',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.deviceName||e.eventDesc||'Unknown'}</span>
                      <span style={{fontSize:'9px',color:'#475569',fontFamily:'monospace',flexShrink:0}}>{e.timestamp?.slice(11,19)||e.userName||''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ maxHeight:'620px', overflowY:'auto' }}>
            {initialLoading && Array.from({length:6}).map((_,i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:'14px', padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', borderLeft:'3px solid rgba(255,255,255,0.06)' }}>
                <div className="skeleton" style={{ width:'60px', height:'20px', borderRadius:'10px', flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div className="skeleton" style={{ height:'13px', width:'55%', marginBottom:'6px' }} />
                  <div className="skeleton" style={{ height:'10px', width:'35%' }} />
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div className="skeleton" style={{ height:'10px', width:'50px', marginBottom:'6px' }} />
                  <div className="skeleton" style={{ height:'18px', width:'70px', borderRadius:'6px' }} />
                </div>
              </div>
            ))}
            {!initialLoading && filtered.length === 0 && (
              <div style={{ padding:'48px', textAlign:'center' }}>
                <div style={{fontSize:'32px',marginBottom:'12px',opacity:0.2}}>🛡️</div>
                <div style={{fontSize:'13px',fontWeight:600,color:'#475569',marginBottom:'6px'}}>
                  {alarmSearch.trim() ? `No alarms matching "${alarmSearch}"` : filter==='active' ? 'No active alarms — all clear' : filter==='acknowledged' ? 'No acknowledged alarms' : filter==='closed' ? 'No closed alarms' : 'No alarms on record'}
                </div>
                <div style={{fontSize:'11px',color:'#334155'}}>
                  {alarmSearch.trim() ? 'Try a different search term or expand the date range.' : filter==='all' ? 'Alarms from your integrated systems will appear here in real time.' : 'Switch filter to see other alarms.'}
                </div>
              </div>
            )}
            {/* ── Grouped view ── */}
            {!initialLoading && groupedView && groupedAlarms && groupedAlarms.map(group => {
              const lead = group[0];
              const isMulti = group.length > 1;
              const maxSev = (['critical','high','medium','low'] as const).find(s => group.some(a => a.severity === s)) || lead.severity;
              const anyActive = group.some(a => a.status === 'active');
              const anyOverdue = group.some(a => a.status==='active' && Date.now()-alarmTsMs(a.time,a.tsMs)>15*60000);
              const groupKey = (lead as any).correlationGroupId || lead.id;
              const isExpanded = expandedGroup === groupKey;
              return (
                <div key={groupKey} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  {/* Cluster header */}
                  <div
                    onClick={() => isMulti ? setExpandedGroup(isExpanded ? null : groupKey) : setSelectedAlarm(lead)}
                    style={{
                      display:'flex', alignItems:'center', gap:'12px',
                      padding:'12px 20px',
                      borderLeft:`3px solid ${anyOverdue ? '#ef4444' : SEV_COLOR[maxSev]}`,
                      background: anyOverdue ? 'rgba(239,68,68,0.07)' : anyActive ? 'rgba(239,68,68,0.03)' : 'rgba(255,255,255,0.01)',
                      cursor:'pointer',
                    }}
                  >
                    <div style={{ flexShrink:0, padding:'2px 8px', borderRadius:'2px', fontSize:'10px', fontWeight:700, background:SEV_BG[maxSev], color:SEV_COLOR[maxSev], minWidth:'60px', textAlign:'center' }}>{maxSev}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' as const }}>
                        <span style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0' }}>{lead.site}</span>
                        {isMulti && (
                          <span style={{ fontSize:'10px', padding:'1px 8px', borderRadius:'10px', background:'rgba(59,130,246,0.18)', color:'#60a5fa', fontWeight:700, border:'1px solid rgba(59,130,246,0.3)' }}>
                            {group.length} correlated alarms
                          </span>
                        )}
                        {anyOverdue && <span style={{ fontSize:'9px', background:'rgba(239,68,68,0.25)', color:'#fca5a5', padding:'2px 8px', borderRadius:'6px', fontWeight:800, border:'1px solid rgba(239,68,68,0.5)' }}>⚠ OVERDUE</span>}
                        {anyActive && !anyOverdue && <span style={{ fontSize:'9px', background:'rgba(239,68,68,0.15)', color:'#ef4444', padding:'1px 6px', borderRadius:'6px', fontWeight:700 }}>ACTIVE</span>}
                      </div>
                      <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                        {isMulti ? group.map(a => a.location).filter((v,i,arr)=>arr.indexOf(v)===i).slice(0,3).join(' · ') : `${lead.location} · ${alarmTypesConfig[lead.type]?.displayName||lead.type}`}
                        {isMulti && group.map(a=>a.location).filter((v,i,arr)=>arr.indexOf(v)===i).length>3 ? ' · …' : ''}
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:'11px', color:'#475569', marginBottom:'4px' }}>{lead.time}</div>
                      {isMulti && <div style={{ fontSize:'10px', color:'#60a5fa' }}>{isExpanded ? '▲ Collapse' : '▼ Expand'}</div>}
                    </div>
                    {!isMulti && (
                      <div style={{ flexShrink:0, display:'flex', flexDirection:'column', gap:'4px' }}>
                        <button onClick={e => { e.stopPropagation(); setSelectedAlarm(lead); }} style={{ padding:'4px 10px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', cursor:'pointer', fontSize:'10px', fontWeight:600 }}>View</button>
                        {lead.status === 'active' && <button onClick={e => { e.stopPropagation(); ack(lead.id); }} style={{ padding:'4px 10px', borderRadius:'2px', border:'1px solid rgba(34,197,94,0.35)', background:'rgba(34,197,94,0.1)', color:'#22c55e', cursor:'pointer', fontSize:'10px', fontWeight:700 }}>✓ ACK</button>}
                      </div>
                    )}
                  </div>
                  {/* Expanded cluster: show individual alarms */}
                  {isMulti && isExpanded && group.map(alarm => {
                    const elMs = Math.max(0, now - alarmTsMs(alarm.time, alarm.tsMs));
                    const ov = alarm.status==='active' && elMs > 15*60000;
                    return (
                      <div key={alarm.id} onClick={() => setSelectedAlarm(alarm)} style={{
                        display:'flex', alignItems:'center', gap:'12px',
                        padding:'10px 20px 10px 36px',
                        borderBottom:'1px solid rgba(255,255,255,0.03)',
                        borderLeft:`3px solid ${ov?'#ef4444':SEV_COLOR[alarm.severity]}`,
                        background: ov?'rgba(239,68,68,0.05)':'rgba(255,255,255,0.015)',
                        cursor:'pointer',
                      }}>
                        <div style={{ flexShrink:0, padding:'1px 7px', borderRadius:'8px', fontSize:'9px', fontWeight:700, background:SEV_BG[alarm.severity], color:SEV_COLOR[alarm.severity], minWidth:'52px', textAlign:'center' }}>{alarm.severity}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'12px', fontWeight:600, color: alarm.status==='closed'?'#475569':'#e2e8f0' }}>{alarmTypesConfig[alarm.type]?.displayName||alarm.type}</div>
                          <div style={{ fontSize:'10px', color:'#64748b' }}>{alarm.location} · {alarm.time}</div>
                        </div>
                        <div style={{ fontSize:'10px', fontWeight:600, padding:'2px 8px', borderRadius:'2px', background: alarm.status==='active'?SEV_BG.critical:alarm.status==='acknowledged'?SEV_BG.medium:'rgba(100,116,139,0.1)', color: alarm.status==='active'?'#ef4444':alarm.status==='acknowledged'?'#3b82f6':'#64748b' }}>{alarm.status}</div>
                        <div style={{ flexShrink:0, display:'flex', gap:'4px' }}>
                          <button onClick={e=>{ e.stopPropagation(); setSelectedAlarm(alarm); }} style={{ padding:'3px 8px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', cursor:'pointer', fontSize:'10px', fontWeight:600 }}>View</button>
                          {alarm.status==='active' && <button onClick={e=>{ e.stopPropagation(); ack(alarm.id); }} style={{ padding:'3px 8px', borderRadius:'2px', border:'1px solid rgba(34,197,94,0.35)', background:'rgba(34,197,94,0.1)', color:'#22c55e', cursor:'pointer', fontSize:'10px', fontWeight:700 }}>✓ ACK</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {/* Select-all header */}
            {!initialLoading && !groupedView && filtered.length > 0 && (
              <div style={{ padding:'6px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', alignItems:'center', gap:'8px', background:'rgba(255,255,255,0.01)' }}>
                <input type="checkbox"
                  checked={selectedAlarmIds.size > 0 && alarmPager.paged.every(a => selectedAlarmIds.has(a.id))}
                  onChange={e => {
                    if (e.target.checked) setSelectedAlarmIds(prev => { const n = new Set(prev); alarmPager.paged.forEach(a => n.add(a.id)); return n; });
                    else setSelectedAlarmIds(prev => { const n = new Set(prev); alarmPager.paged.forEach(a => n.delete(a.id)); return n; });
                  }}
                  style={{ cursor:'pointer', accentColor:'#3b82f6' }}
                />
                <span style={{ fontSize:'10px', color:'#475569' }}>Select all on page ({alarmPager.paged.length})</span>
                {selectedAlarmIds.size > 0 && <span style={{ fontSize:'10px', color:'#60a5fa', fontWeight:700 }}>{selectedAlarmIds.size} selected total</span>}
              </div>
            )}
            {/* ── Flat (default) view ── */}
            {!initialLoading && !groupedView && alarmPager.paged.map(alarm => {
              const elapsedMs = Math.max(0, now - alarmTsMs(alarm.time, alarm.tsMs));
              const isCritActive = alarm.status==='active' && alarm.severity==='critical';
              const isHighActive = alarm.status==='active' && alarm.severity==='high';
              const slaBreach = alarm.status==='active' && elapsedMs > 15*60000;
              const isOverdue = slaBreach; // active + SLA breached = overdue ack required
              const isSelected = selectedAlarmIds.has(alarm.id);
              return (
              <div key={alarm.id} onClick={() => setSelectedAlarm(alarm)} style={{
                display:'flex', alignItems:'flex-start', gap:'14px',
                padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)',
                borderLeft:`3px solid ${isOverdue ? '#ef4444' : SEV_COLOR[alarm.severity]}`,
                background: isOverdue ? 'rgba(239,68,68,0.07)' : isCritActive ? 'rgba(239,68,68,0.04)' : newIds.has(alarm.id) ? 'rgba(59,130,246,0.06)' : alarm.status==='closed' ? 'transparent' : 'rgba(255,255,255,0.01)',
                transition:'background 0.3s', cursor:'pointer', position:'relative',
                animation: isOverdue ? 'critGlow 1.5s ease-in-out infinite' : isCritActive ? 'critGlow 2.5s ease-in-out infinite' : isHighActive ? 'highGlow 3s ease-in-out infinite' : 'none',
                outline: isOverdue ? '1px solid rgba(239,68,68,0.25)' : 'none',
              }}
                onMouseOver={e => { if (!newIds.has(alarm.id) && !isCritActive && !isOverdue) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.025)'; }}
                onMouseOut={e => { (e.currentTarget as HTMLElement).style.background = isOverdue ? 'rgba(239,68,68,0.07)' : isCritActive ? 'rgba(239,68,68,0.04)' : newIds.has(alarm.id) ? 'rgba(59,130,246,0.06)' : alarm.status==='closed' ? 'transparent' : 'rgba(255,255,255,0.01)'; }}
              >
                <input type="checkbox" checked={isSelected} onClick={e => e.stopPropagation()} onChange={e => {
                  const next = new Set(selectedAlarmIds);
                  if (e.target.checked) next.add(alarm.id); else next.delete(alarm.id);
                  setSelectedAlarmIds(next);
                }} style={{ flexShrink:0, cursor:'pointer', accentColor:'#3b82f6', marginTop:'2px' }} />
                <div style={{ flexShrink:0, padding:'2px 8px', borderRadius:'2px', fontSize:'10px', fontWeight:700, background:SEV_BG[alarm.severity], color:SEV_COLOR[alarm.severity], marginTop:'2px', minWidth:'60px', textAlign:'center' }}>{alarm.severity}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  {/* ── Site badge — prominent, first thing operators see ── */}
                  <div style={{ marginBottom:'8px', display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ width:'11px', height:'11px', borderRadius:'50%', background:SEV_COLOR[alarm.severity], flexShrink:0, boxShadow:`0 0 8px ${SEV_COLOR[alarm.severity]}80`, display:'inline-block', animation:(isCritActive||isHighActive)?'pulse 1.5s infinite':'none' }} />
                    <span style={{ fontSize:'18px', fontWeight:800, color:'#ffffff', lineHeight:1.1, letterSpacing:'-0.02em' }}>{alarm.site}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px', flexWrap:'wrap' as const }}>
                    <span style={{ fontSize:'13px', fontWeight:600, color: alarm.status==='closed'?'#475569':'#e2e8f0' }}>{alarmTypesConfig[alarm.type]?.displayName || alarm.type}</span>
                    {alarmTypesConfig[alarm.type]?.category && (() => {
                      const cat = alarmTypesConfig[alarm.type].category;
                      const catColor: Record<string,string> = { Security:'#ef4444', Technical:'#f59e0b', Fire:'#f97316', Medical:'#22c55e', Access:'#3b82f6', Environmental:'#06b6d4', Other:'#64748b' };
                      return <span style={{ fontSize:'9px', fontWeight:700, padding:'1px 6px', borderRadius:'6px', background:`${catColor[cat]||'#64748b'}18`, color:catColor[cat]||'#64748b', border:`1px solid ${catColor[cat]||'#64748b'}35` }}>{cat}</span>;
                    })()}
                    {newIds.has(alarm.id) && <span style={{ fontSize:'9px', background:'rgba(59,130,246,0.2)', color:'#3b82f6', padding:'1px 6px', borderRadius:'6px', fontWeight:700 }}>NEW</span>}
                    {isOverdue && (
                      <span style={{ fontSize:'9px', background:'rgba(239,68,68,0.25)', color:'#fca5a5', padding:'2px 8px', borderRadius:'6px', fontWeight:800, border:'1px solid rgba(239,68,68,0.5)', letterSpacing:'0.04em', animation:'pulse 1s infinite' }}>
                        ⚠ OVERDUE ACK
                      </span>
                    )}
                    {!isOverdue && alarm.status==='active' && (isCritActive||isHighActive) && (
                      <span style={{ fontSize:'9px', background:'rgba(239,68,68,0.15)', color:'#ef4444', padding:'1px 6px', borderRadius:'6px', fontWeight:700, animation:'pulse 1.5s infinite' }}>⚠ RESPONSE REQUIRED</span>
                    )}
                    {slaBreach && !isOverdue && <span style={{ fontSize:'9px', background:'rgba(239,68,68,0.2)', color:'#fca5a5', padding:'1px 6px', borderRadius:'6px', fontWeight:700, border:'1px solid rgba(239,68,68,0.3)' }}>SLA BREACH</span>}
                    {alarm.source && <span style={{ fontSize:'9px', padding:'1px 6px', borderRadius:'6px', fontWeight:700,
                      background: alarm.source.startsWith('ArmorIP') ? 'rgba(168,85,247,0.15)' : 'rgba(59,130,246,0.1)',
                      color:      alarm.source.startsWith('ArmorIP') ? '#c084fc' : '#60a5fa',
                    }}>{alarm.source}</span>}
                    {alarm.repeatCount && alarm.repeatCount > 1 && (() => {
                      const rc = alarm.repeatCount!;
                      if (rc >= 10) return (
                        <span style={{ fontSize:'9px', padding:'2px 8px', borderRadius:'6px', fontWeight:800, background:'rgba(239,68,68,0.2)', color:'#ef4444', border:'1px solid rgba(239,68,68,0.45)', animation:'pulse 0.8s infinite', letterSpacing:'0.04em', display:'flex', alignItems:'center', gap:'3px' }}>
                          ⚡ STORM ×{rc}
                        </span>
                      );
                      if (rc >= 5) return (
                        <span style={{ fontSize:'9px', padding:'2px 8px', borderRadius:'6px', fontWeight:800, background:'rgba(249,115,22,0.2)', color:'#fb923c', border:'1px solid rgba(249,115,22,0.4)', animation:'pulse 1.2s infinite', letterSpacing:'0.03em' }}>
                          ⚠ ×{rc}
                        </span>
                      );
                      return (
                        <span style={{ fontSize:'9px', padding:'1px 7px', borderRadius:'6px', fontWeight:700, background:'rgba(245,158,11,0.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.25)' }}>
                          ×{rc}
                        </span>
                      );
                    })()}
                    {alarm.jobNumber && <span style={{ fontSize:'9px', color:'#3b82f6', fontFamily:'monospace' }}>{alarm.jobNumber}</span>}
                  </div>
                  <div style={{ fontSize:'11px', color:'#64748b' }}>{alarm.location}</div>
                  {/* Camera names row */}
                  {(() => {
                    const camList = alarm.cameraId
                      ? [alarm.cameraId, ...(LINKED_CAMERAS[alarm.site] || []).filter(c => c !== alarm.cameraId)]
                      : (LINKED_CAMERAS[alarm.site] || []);
                    if (!camList.length) return null;
                    return (
                      <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginTop:'4px' }}>
                        {camList.slice(0, 3).map(cam => (
                          <span key={cam} onClick={e => { e.stopPropagation(); setSelectedAlarm(alarm); }}
                            title={`View camera: ${cam} — click to open alarm detail`}
                            style={{ fontSize:'9px', color:'#3b82f6', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'4px', padding:'1px 6px', fontFamily:'monospace', cursor:'pointer', display:'flex', alignItems:'center', gap:'3px' }}>
                            {'\u{1F4F7}'} {cam}
                          </span>
                        ))}
                        {camList.length > 3 && <span style={{ fontSize:'9px', color:'#475569' }}>+{camList.length-3}</span>}
                      </div>
                    );
                  })()}
                  {/* ── Escalation chain bar (active, past 5-min initial response window) ── */}
                  {alarm.status === 'active' && elapsedMs > 5 * 60000 && (() => {
                    const esc = getEscalation(elapsedMs);
                    const msNext = msUntilNext(elapsedMs, esc);
                    const lvlIdx = ESCALATION_LEVELS.indexOf(esc);
                    // progress within current window
                    const windowStart = esc.threshold * 60000;
                    const windowEnd = esc.next ? esc.next * 60000 : windowStart + 30 * 60000;
                    const pct = Math.min(100, Math.round(((elapsedMs - windowStart) / (windowEnd - windowStart)) * 100));
                    return (
                      <div style={{ marginTop:'6px', background:'rgba(0,0,0,0.25)', borderRadius:'6px', padding:'5px 8px', border:`1px solid ${esc.color}30` }}>
                        {/* Level indicators */}
                        <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'4px' }}>
                          {ESCALATION_LEVELS.slice(0,5).map((l,i) => (
                            <div key={i} title={l.label} style={{
                              flex:1, height:'4px', borderRadius:'2px',
                              background: i <= lvlIdx ? l.color : 'rgba(255,255,255,0.1)',
                              transition:'background 0.5s',
                            }} />
                          ))}
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <span style={{ fontSize:'9px', fontWeight:700, color:esc.color, letterSpacing:'0.03em' }}>{esc.label}</span>
                          {msNext !== null && msNext > 0 ? (
                            <span style={{ fontSize:'9px', color:'#94a3b8', fontFamily:'monospace' }}>
                              ↑ {esc.nextLabel} in {fmtElapsed(msNext)}
                            </span>
                          ) : msNext === 0 ? (
                            <span style={{ fontSize:'9px', color:'#ef4444', fontWeight:700, animation:'pulse 1s infinite' }}>ESCALATING NOW</span>
                          ) : (
                            <span style={{ fontSize:'9px', color:'#ef4444', fontWeight:700 }}>MAX ESCALATION</span>
                          )}
                        </div>
                        {/* Progress bar within current window */}
                        <div style={{ marginTop:'3px', height:'2px', background:'rgba(255,255,255,0.08)', borderRadius:'1px', overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:esc.color, borderRadius:'1px', transition:'width 1s linear' }} />
                        </div>
                      </div>
                    );
                  })()}
                  {alarm.operator && (
                    <div style={{ fontSize:'11px', color:'#64748b', marginTop:'4px', display:'flex', alignItems:'center', gap:'5px' }}>
                      <span style={{ width:'18px', height:'18px', borderRadius:'50%', background:'rgba(148,163,184,0.15)', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'9px', fontWeight:800, color:'#94a3b8', flexShrink:0 }}>{(alarm.operator||'?').charAt(0).toUpperCase()}</span>
                      {alarm.operator}
                    </div>
                  )}
                  {/* ── Bottom action row ── */}
                  <div className="alarm-action-bar">
                    {alarm.status==='active' && (
                      <button className="btn-ack" onClick={e=>{e.stopPropagation();setSelectedAlarm(alarm);setPanelDefaultTab('sop');}}>✓ ACKNOWLEDGE</button>
                    )}
                    {alarm.status==='active' && !alarm.jobNumber && (
                      <button className="btn-escalate" onClick={e=>{e.stopPropagation(); window.location.href=`/m/jobs?alarm=${alarm.id}&type=${encodeURIComponent(alarm.type)}&site=${encodeURIComponent(alarm.site)}&loc=${encodeURIComponent(alarm.location)}&sev=${alarm.severity}`;}}>↑ ESCALATE</button>
                    )}
                    {alarm.status==='acknowledged' && (
                      <button className="btn-close" onClick={e=>{e.stopPropagation();close(alarm.id);}}>✕ CLOSE</button>
                    )}
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  {alarm.status==='active' && elapsedMs>0 && <div style={{ fontSize:'12px', color:'#94a3b8', fontWeight:600, marginBottom:'1px', fontVariantNumeric:'tabular-nums' }}>{fmtElapsed(elapsedMs)} ago</div>}
                  <div style={{ fontSize:'10px', color:'#475569', fontVariantNumeric:'tabular-nums', marginBottom:'4px' }}>{alarm.time}</div>
                  {alarm.status === 'active' && alarm.tsMs && alarm.tsMs > 0 && (
                    <div style={{ fontSize:'11px', color:isOverdue ? '#dc2626' : slaColor(elapsedMs), fontVariantNumeric:'tabular-nums', fontWeight: elapsedMs >= 600000 ? 700 : 600, marginBottom:'4px', fontFamily:'monospace' }}>
                      ⏱ {fmtSla(alarm.tsMs, now)}
                    </div>
                  )}
                  <div style={{ fontSize:'10px', fontWeight:600, padding:'2px 8px', borderRadius:'2px', background: alarm.status==='active'?SEV_BG.critical:alarm.status==='acknowledged'?SEV_BG.medium:'rgba(100,116,139,0.1)', color: alarm.status==='active'?'#ef4444':alarm.status==='acknowledged'?'#3b82f6':'#64748b' }}>
                    {alarm.status}
                  </div>
                </div>
                <div style={{ flexShrink:0, display:'flex', flexDirection:'column', gap:'4px' }}>
                  <button onClick={e => { e.stopPropagation(); setSelectedAlarm(alarm); }} style={{ padding:'4px 10px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', cursor:'pointer', fontSize:'10px', fontWeight:600 }}>View</button>
                  <button onClick={e => {
                    e.stopPropagation();
                    setShowTimeline(alarm);
                    setTimelineData(null);
                    const tok = localStorage.getItem('lemai_token') || '';
                    fetch(`/api/armorip/alarms/${alarm.id}/timeline`, { headers: { Authorization: `Bearer ${tok}` } })
                      .then(r => r.ok ? r.json() : null)
                      .then(d => { if (d?.events) setTimelineData(d.events); else setTimelineData([]); })
                      .catch(() => setTimelineData([]));
                  }} style={{ padding:'4px 8px', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.03)', color:'#475569', cursor:'pointer', fontSize:'10px', whiteSpace:'nowrap' as const }}>
                    ⏱ TL
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          <div style={{ padding:'10px 20px', borderTop:'1px solid rgba(255,255,255,0.04)', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
            {groupedView
              ? <span style={{ fontSize:'11px', color:'#475569' }}>{groupedAlarms?.length ?? 0} incident clusters · {filtered.length} total alarms</span>
              : <Pagination total={alarmPager.total} page={alarmPager.page} pageSize={alarmPager.pageSize} onPage={alarmPager.onPage} onPageSize={alarmPager.onPageSize} />
            }
            <button onClick={() => exportCSV(`alarms-${csvDateStamp()}`,
              ['ID','Type','Site','Location','Time','Severity','Status','Operator'],
              filtered.map(a=>[a.id,a.type,a.site,a.location,a.time,a.severity,a.status,a.operator||''])
            )} style={{ padding:'4px 12px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'6px', color:'#22c55e', fontSize:'10px', fontWeight:600, cursor:'pointer' }}>
              ↓ Filtered CSV
            </button>
            <button onClick={() => {
              const tok = localStorage.getItem('lemai_token') || '';
              fetch('/api/armorip/alarms/export.csv?limit=50000', { headers: { Authorization: `Bearer ${tok}` } })
                .then(r => r.blob())
                .then(blob => {
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `lemai-alarm-history-${csvDateStamp()}.csv`;
                  a.click();
                });
            }} style={{ padding:'4px 12px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.25)', borderRadius:'6px', color:'#60a5fa', fontSize:'10px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' as const }}>
              ↓ Full History
            </button>
          </div>
        </div>
      )}

      {/* ── Site Maps Tab ── */}
      {mainTab === 'map' && (
        <div style={{ display:'flex', gap:'14px' }}>
          {/* Left: Site/Controller list with search */}
          <div style={{ width:'210px', flexShrink:0 }}>
            <div style={{ fontSize:'10px', fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px' }}>Sites / Controllers</div>
            <input
              value={socMapSearch}
              onChange={e=>setSocMapSearch(e.target.value)}
              placeholder="Search sites..."
              style={{ width:'100%', boxSizing:'border-box', padding:'6px 10px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'white', fontSize:'11px', outline:'none', marginBottom:'8px' }}
            />
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              {(socSites.length > 0 ? socSites : SITES.map((s,i)=>({id:String(i+1),name:s,online:true})))
                .filter(site => !socMapSearch || site.name.toLowerCase().includes(socMapSearch.toLowerCase()))
                .map(site => {
                  const siteAlarms = alarms.filter(a => a.status==='active' && a.site.toLowerCase().includes(site.name.toLowerCase().split(' ')[0]));
                  const isSelected = socMapCtrl === site.id;
                  return (
                    <div key={site.id} onClick={()=>setSocMapCtrl(prev => prev===site.id ? null : site.id)}
                      style={{ padding:'10px 12px', borderRadius:'9px', cursor:'pointer', transition:'all 0.15s',
                        background: isSelected ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)',
                        border: isSelected ? '1px solid rgba(59,130,246,0.35)' : '1px solid rgba(255,255,255,0.06)',
                      }}
                      onMouseOver={e=>{ if(!isSelected)(e.currentTarget as HTMLElement).style.borderColor='rgba(59,130,246,0.25)'; }}
                      onMouseOut={e=>{ if(!isSelected)(e.currentTarget as HTMLElement).style.borderColor='rgba(255,255,255,0.06)'; }}
                    >
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px' }}>
                        <div style={{ width:'6px', height:'6px', borderRadius:'50%', background:(site as any).online!==false?'#22c55e':'#ef4444', flexShrink:0 }} />
                        <span style={{ fontSize:'11px', fontWeight:600, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{site.name}</span>
                      </div>
                      {siteAlarms.length > 0
                        ? <div style={{ fontSize:'9px', color:'#ef4444', fontWeight:600, marginLeft:'12px' }}>{siteAlarms.length} active alarm{siteAlarms.length>1?'s':''}</div>
                        : <div style={{ fontSize:'9px', color:'#334155', marginLeft:'12px' }}>All clear</div>
                      }
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Right: Full-featured map (same as Access Control) */}
          <div style={{ flex:1, minWidth:0 }}>
            {socMapCtrl === null ? (
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', height:'460px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'12px' }}>
                <div style={{ fontSize:'36px', opacity:0.15 }}>🗺️</div>
                <div style={{ fontSize:'13px', color:'#475569', fontWeight:600 }}>Select a site to view its map</div>
                <div style={{ fontSize:'11px', color:'#334155' }}>Maps configured in Access Control are shared here</div>
              </div>
            ) : (
              <div>
                {/* Map toolbar */}
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px', flexWrap:'wrap' }}>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'#e2e8f0', flex:1 }}>
                    {socSites.find(s=>s.id===socMapCtrl)?.name || 'Site Map'}
                  </div>

                  {/* Zone toggle */}
                  <button onClick={()=>{ const n=!socShowZones; setSocShowZones(n); localStorage.setItem('siteMapShowZones',String(n)); }}
                    style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:600, cursor:'pointer', background: socShowZones?'rgba(59,130,246,0.15)':'rgba(255,255,255,0.04)', border: socShowZones?'1px solid rgba(59,130,246,0.3)':'1px solid rgba(255,255,255,0.1)', color: socShowZones?'#93c5fd':'#64748b' }}>
                    Zones {socShowZones ? 'On' : 'Off'}
                  </button>
                  <span style={{ fontSize:'10px', color:'#334155', padding:'4px 8px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'6px' }}>
                    Edit maps in Access Control
                  </span>

                  {/* Lockdown */}
                  {isProtegeOrg && (
                    <button onClick={()=>handleLockdownController(socSites.find(s=>s.id===socMapCtrl) || {id:socMapCtrl!,name:'Site'})}
                      style={{ padding:'5px 12px', borderRadius:'7px', border:'1px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                      🔒 Lockdown Site
                    </button>
                  )}
                </div>

                {/* Map component - same data as Access Control */}
                <SiteMap
                  mode="soc"
                  editLayout={false}
                  bgImage={socMapBgImage || undefined}
                  showZones={socShowZones}
                  liveDoorStatus={liveDoorStatus as any}
                  onDoorControl={doorControl}
                  alarms={alarms.filter(a => a.status==='active').map(a => ({
                    id: a.id, type: a.type,
                    zone: a.location.includes('Server')?'server':'lobby',
                    severity: a.severity,
                  }) as MapAlarm)}
                  doors={Object.entries(socDoorPositions).map(([id, pos]) => ({
                    id, name: `Door ${id}`, zone:'lobby', status:'locked' as const, x: pos.x, y: pos.y, horizontal:false,
                  }))}
                  cameras={Object.entries(socCameraPositions).map(([id, pos]) => ({
                    id, name: id, zone:'lobby', status:'online' as const, cx: pos.x, cy: pos.y,
                  }))}
                  onCameraClick={_cam => {}}
                  onPositionChange={(id, x, y) => {
                    const newPos = {...socDoorPositions, [id]: {x, y}};
                    setSocDoorPositions(newPos);
                    localStorage.setItem(socMapCtrl ? `siteMapDoorPositions_${socMapCtrl}` : 'siteMapDoorPositions', JSON.stringify(newPos));
                  }}
                  onRemoveDoor={id => {
                    const newPos = {...socDoorPositions}; delete newPos[id];
                    setSocDoorPositions(newPos);
                    localStorage.setItem(socMapCtrl ? `siteMapDoorPositions_${socMapCtrl}` : 'siteMapDoorPositions', JSON.stringify(newPos));
                  }}
                  onCameraPositionChange={(id, x, y) => {
                    const newPos = {...socCameraPositions, [id]: {x, y}};
                    setSocCameraPositions(newPos);
                    localStorage.setItem(socMapCtrl ? `siteMapCameraPositions_${socMapCtrl}` : 'siteMapCameraPositions', JSON.stringify(newPos));
                  }}
                  onRemoveCamera={id => {
                    const newPos = {...socCameraPositions}; delete newPos[id];
                    setSocCameraPositions(newPos);
                    localStorage.setItem(socMapCtrl ? `siteMapCameraPositions_${socMapCtrl}` : 'siteMapCameraPositions', JSON.stringify(newPos));
                  }}
                  height={460}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Guard Tours Tab ── */}
      {mainTab === 'tours' && isVisible('tours') && (
        <div>
          {/* Summary row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'16px' }}>
            {[
              { label:'Active Tours',    value:guardTours.filter(t=>t.status==='active').length,    color:'#3b82f6' },
              { label:'Completed Today', value:guardTours.filter(t=>t.status==='completed').length,  color:'#22c55e' },
              { label:'Scheduled',       value:guardTours.filter(t=>t.status==='scheduled').length,  color:'#64748b' },
              { label:'Overdue',         value:guardTours.filter(t=>t.status==='overdue').length,    color:'#ef4444' },
            ].map(s=>(
              <div key={s.label} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'14px' }}>
                <div style={{ fontSize:'24px', fontWeight:700, color:s.color }}>{s.value}</div>
                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'3px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Tour list */}
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            {guardTours.map(tour => {
              const statusColors: Record<string,[string,string]> = {
                completed: ['#22c55e','rgba(34,197,94,0.08)'],
                active:    ['#3b82f6','rgba(59,130,246,0.08)'],
                scheduled: ['#64748b','rgba(100,116,139,0.06)'],
                overdue:   ['#ef4444','rgba(239,68,68,0.08)'],
              };
              const [sc, sbg] = statusColors[tour.status] || ['#64748b','transparent'];
              const doneCount = tour.checkpoints.filter(c=>c.completed).length;
              return (
                <div key={tour.id} style={{ background:'rgba(255,255,255,0.03)', border:`1px solid ${sc}30`, borderRadius:'12px', overflow:'hidden' }}>
                  {/* Tour header */}
                  <div style={{ padding:'12px 16px', background:sbg, display:'flex', alignItems:'center', gap:'12px' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                        <span style={{ fontSize:'13px', fontWeight:700, color:'#e2e8f0' }}>{tour.site}</span>
                        <span style={{ fontSize:'9px', fontWeight:700, padding:'1px 7px', borderRadius:'8px', background:`${sc}20`, color:sc, textTransform:'uppercase' }}>{tour.status}</span>
                        {tour.status==='overdue' && <span style={{ fontSize:'9px', color:'#ef4444', fontWeight:700 }}>⚠ OFFICER NOT RESPONDING</span>}
                      </div>
                      <div style={{ fontSize:'11px', color:'#64748b' }}>
                        {tour.officer} · {tour.startTime} – {tour.endTime} · {doneCount}/{tour.checkpoints.length} checkpoints
                      </div>
                    </div>
                    {/* Progress ring area */}
                    <div style={{ textAlign:'center', flexShrink:0 }}>
                      <div style={{ fontSize:'18px', fontWeight:700, color:sc }}>{tour.completionPct}%</div>
                      <div style={{ fontSize:'9px', color:'#475569' }}>complete</div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ width:'100px', flexShrink:0 }}>
                      <div style={{ background:'rgba(255,255,255,0.06)', borderRadius:'4px', height:'5px', overflow:'hidden' }}>
                        <div style={{ width:`${tour.completionPct}%`, height:'100%', background:sc, borderRadius:'4px', transition:'width 0.3s' }} />
                      </div>
                    </div>
                    {tour.status === 'active' && (
                      <button onClick={async () => {
                        const now = new Date().toTimeString().slice(0,5);
                        const updated = { ...tour, status:'completed' as const, completionPct:100, checkpoints: tour.checkpoints.map(c=>({...c,completed:true,completedAt:c.completedAt||now})) };
                        setGuardTours(prev=>prev.map(t=>t.id===tour.id ? updated : t));
                        toast(`Tour completed: ${tour.site}`, 'success');
                        if (!isDemoOrg) {
                          const tok = localStorage.getItem('lemai_token') || '';
                          await fetch(`/api/tours/${tour.id}`, { method:'PATCH', headers:{'Content-Type':'application/json',Authorization:`Bearer ${tok}`}, body:JSON.stringify({status:'completed',completionPct:100,checkpoints:updated.checkpoints}) }).catch(()=>{});
                        }
                      }} style={{ padding:'5px 12px', borderRadius:'7px', border:'1px solid rgba(34,197,94,0.35)', background:'rgba(34,197,94,0.1)', color:'#22c55e', fontSize:'11px', fontWeight:700, cursor:'pointer', flexShrink:0, whiteSpace:'nowrap' }}>
                        Complete Tour
                      </button>
                    )}
                  </div>

                  {/* Checkpoints */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'0', borderTop:'1px solid rgba(255,255,255,0.04)' }}>
                    {tour.checkpoints.map((cp, ci) => (
                      <div key={cp.id} onClick={async ()=>{
                        if (tour.status === 'active' && !cp.completed) {
                          const now = new Date().toTimeString().slice(0,5);
                          setGuardTours(prev=>prev.map(t=>t.id===tour.id?{...t,checkpoints:t.checkpoints.map(c=>c.id===cp.id?{...c,completed:true,completedAt:now}:c),completionPct:Math.round((t.checkpoints.filter(c=>c.id===cp.id||c.completed).length/t.checkpoints.length)*100)}:t));
                          if (!isDemoOrg) {
                            const tok = localStorage.getItem('lemai_token') || '';
                            await fetch(`/api/tours/${tour.id}/checkpoint/${cp.id}`, { method:'POST', headers:{Authorization:`Bearer ${tok}`} }).catch(()=>{});
                          }
                        }
                      }}
                        style={{ padding:'10px 14px', borderRight: ci%2===0?'1px solid rgba(255,255,255,0.04)':'none', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:tour.status==='active'&&!cp.completed?'pointer':'default', background:cp.completed?'rgba(34,197,94,0.04)':cp.overdue?'rgba(239,68,68,0.04)':'transparent', transition:'background 0.15s' }}
                        onMouseOver={e=>{ if(tour.status==='active'&&!cp.completed)(e.currentTarget as HTMLElement).style.background='rgba(255,255,255,0.04)'; }}
                        onMouseOut={e=>{ (e.currentTarget as HTMLElement).style.background=cp.completed?'rgba(34,197,94,0.04)':cp.overdue?'rgba(239,68,68,0.04)':'transparent'; }}
                      >
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px' }}>
                          <div style={{ width:'14px', height:'14px', borderRadius:'50%', background:cp.completed?'#22c55e':cp.overdue?'#ef4444':'rgba(255,255,255,0.1)', border:`1px solid ${cp.completed?'#22c55e':cp.overdue?'#ef4444':'rgba(255,255,255,0.15)'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8px', color:'white', flexShrink:0 }}>
                            {cp.completed ? '✓' : ci+1}
                          </div>
                          <span style={{ fontSize:'11px', fontWeight:600, color:cp.completed?'#64748b':cp.overdue?'#f87171':'#e2e8f0' }}>{cp.name}</span>
                        </div>
                        <div style={{ fontSize:'9px', color:'#334155', marginLeft:'20px' }}>{cp.location}</div>
                        <div style={{ fontSize:'9px', color:cp.completed?'#22c55e':cp.overdue?'#ef4444':'#475569', marginLeft:'20px', marginTop:'2px' }}>
                          Expected: {cp.expected}{cp.completedAt ? ` · Done: ${cp.completedAt}` : ''}
                          {cp.overdue && !cp.completed && ' ⚠ OVERDUE'}
                          {tour.status==='active'&&!cp.completed&&!cp.overdue && ' — tap to check in'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add patrol button */}
          <button onClick={()=>{ const newTour: GuardTour = { id:`T-00${guardTours.length+1}`, officer:'Unassigned', site:'All Sites', startTime:new Date().toTimeString().slice(0,5), endTime:'', status:'scheduled', completionPct:0, checkpoints:[] }; setGuardTours(prev=>[...prev, newTour]); toast('New patrol created', 'success'); }}
            style={{ marginTop:'12px', padding:'8px 20px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.25)', borderRadius:'8px', color:'#3b82f6', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
            + Schedule New Patrol
          </button>
        </div>
      )}

      {/* ── Shift Handover Modal ── */}
      {showHandoverModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }} onClick={()=>setShowHandoverModal(false)}>
          <div style={{ background:'#0d1321', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'16px', width:'560px', maxWidth:'95vw', maxHeight:'85vh', overflow:'hidden', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Shift Handover</div>
                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>Current shift: 07:00 – 15:00 · Outgoing: {authUser?.email || 'Unknown'}</div>
              </div>
              <button onClick={()=>setShowHandoverModal(false)} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', color:'#64748b', width:'28px', height:'28px', fontSize:'14px', cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ overflowY:'auto', padding:'16px 20px', flex:1 }}>
              {/* Shift summary stats */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'16px' }}>
                {[
                  { label:'Alarms handled',  value:alarms.filter(a=>a.status==='closed'||a.status==='acknowledged').length, color:'#3b82f6' },
                  { label:'Still open',       value:alarms.filter(a=>a.status==='active').length,  color:'#ef4444' },
                  { label:'Tours completed',  value:guardTours.filter(t=>t.status==='completed').length, color:'#22c55e' },
                ].map(s=>(
                  <div key={s.label} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'12px', textAlign:'center' }}>
                    <div style={{ fontSize:'22px', fontWeight:700, color:s.color }}>{s.value}</div>
                    <div style={{ fontSize:'10px', color:'#64748b', marginTop:'2px' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Open alarms list */}
              {alarms.filter(a=>a.status==='active').length > 0 && (
                <div style={{ background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'10px', padding:'12px', marginBottom:'14px' }}>
                  <div style={{ fontSize:'11px', fontWeight:700, color:'#f87171', marginBottom:'8px' }}>Open alarms to hand over</div>
                  {alarms.filter(a=>a.status==='active').slice(0,5).map(a=>(
                    <div key={a.id} style={{ fontSize:'11px', color:'#94a3b8', padding:'4px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', justifyContent:'space-between' }}>
                      <span>{a.type} — {a.site}</span>
                      <span style={{ color:SEV_COLOR[a.severity], fontWeight:700, fontSize:'10px' }}>{a.severity}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Handover notes */}
              <div style={{ marginBottom:'14px' }}>
                <div style={{ fontSize:'10px', color:'#475569', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>Handover Notes</div>
                <textarea value={handoverNotes} onChange={e=>setHandoverNotes(e.target.value)} rows={4}
                  placeholder="Briefing for incoming operator — pending tasks, site issues, follow-ups, special instructions..."
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'10px 12px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box', resize:'none' }} />
              </div>

              {/* Incoming operator */}
              <div style={{ marginBottom:'14px' }}>
                <div style={{ fontSize:'10px', color:'#475569', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>Incoming Operator</div>
                <select value={handoverIncomingOp} onChange={e=>setHandoverIncomingOp(e.target.value)} style={{ width:'100%', padding:'8px 12px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', color:'#e2e8f0', fontSize:'12px', outline:'none' }}>
                  <option value="" style={{background:'#1e293b'}}>Select incoming operator…</option>
                  {operatorNames.map(op=><option key={op} style={{background:'#1e293b'}}>{op}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding:'14px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', gap:'8px' }}>
              <button onClick={()=>setShowHandoverModal(false)} style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.08)', background:'transparent', color:'#64748b', fontSize:'12px', cursor:'pointer' }}>Cancel</button>
              <button onClick={async ()=>{
                const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token');
                try {
                  const res = await fetch('/api/handover', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                      outgoing_operator: authUser?.email || 'Unknown',
                      incoming_operator: handoverIncomingOp || 'Unassigned',
                      notes: handoverNotes,
                      open_alarm_count: alarms.filter(a => a.status === 'active').length,
                      acknowledged_count: alarms.filter(a => a.status === 'acknowledged').length,
                      tours_completed: guardTours.filter(t => t.status === 'completed').length,
                    })
                  });
                  if (!res.ok) throw new Error('Failed to save handover');
                  localStorage.removeItem('lemai_handover_notes');
                  setHandoverSigned(true);
                  setShowHandoverModal(false);
                  toast('Shift handover signed and saved', 'success');
                  auditLog({ category:'access', action:'HANDOVER', operator: authUser?.email || 'Unknown', target: handoverIncomingOp || 'Unassigned', detail:`Notes: ${handoverNotes.substring(0, 100)}`, severity:'info' });
                } catch {
                  toast('Failed to save handover — check connection', 'error');
                }
              }}
                style={{ flex:2, padding:'8px', borderRadius:'8px', border:'none', background:'linear-gradient(135deg,#22c55e,#15803d)', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
                Sign & Submit Handover
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SLA Dashboard Tab ── */}
      {mainTab === 'sla' && isVisible('sla') && (
        <div>
          {/* Supervisor toolbar */}
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
            <div style={{ fontSize:'12px', color:'#64748b', flex:1 }}>
              SLA calculations are derived from real alarm timestamps. MTTA/MTTR update as alarms are acknowledged and resolved.
            </div>
            <button onClick={() => setIsSupervisor(s => !s)} style={{
              padding:'5px 14px', borderRadius:'7px', fontSize:'11px', fontWeight:700, cursor:'pointer',
              border:`1px solid ${isSupervisor ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.1)'}`,
              background: isSupervisor ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.04)',
              color: isSupervisor ? '#f59e0b' : '#64748b',
            }}>
              {isSupervisor ? '★ Supervisor Mode' : 'Operator Mode'}
            </button>
            {/* SLA Target editor — supervisor only */}
            {isSupervisor && (
              <div style={{ display:'flex', alignItems:'center', gap:'6px', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'8px', padding:'5px 10px' }}>
                <span style={{ fontSize:'11px', color:'#94a3b8' }}>Response target:</span>
                {editingSlaTarget ? (
                  <>
                    <input type="number" min={1} max={60} value={slaTargetDraft}
                      onChange={e => setSlaTargetDraft(Number(e.target.value))}
                      style={{ width:'48px', padding:'2px 6px', borderRadius:'5px', border:'1px solid rgba(245,158,11,0.4)', background:'rgba(0,0,0,0.3)', color:'#f59e0b', fontSize:'12px', fontWeight:700 }}
                    />
                    <span style={{ fontSize:'11px', color:'#64748b' }}>min</span>
                    <button onClick={() => { setSlaTarget(slaTargetDraft); localStorage.setItem('socSlaTarget', String(slaTargetDraft)); setEditingSlaTarget(false); toast(`SLA target updated to ${slaTargetDraft} min`, 'success'); }}
                      style={{ padding:'2px 8px', borderRadius:'5px', border:'none', background:'#f59e0b', color:'black', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>Save</button>
                    <button onClick={() => setEditingSlaTarget(false)}
                      style={{ padding:'2px 8px', borderRadius:'5px', border:'1px solid rgba(255,255,255,0.08)', background:'transparent', color:'#64748b', fontSize:'11px', cursor:'pointer' }}>✕</button>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize:'13px', fontWeight:700, color:'#f59e0b' }}>{slaTarget} min</span>
                    <button onClick={() => { setSlaTargetDraft(slaTarget); setEditingSlaTarget(true); }}
                      style={{ padding:'2px 8px', borderRadius:'5px', border:'1px solid rgba(245,158,11,0.3)', background:'transparent', color:'#f59e0b', fontSize:'10px', cursor:'pointer' }}>Edit</button>
                  </>
                )}
              </div>
            )}
            {!isSupervisor && (
              <div style={{ display:'flex', alignItems:'center', gap:'6px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', padding:'5px 10px' }}>
                <span style={{ fontSize:'11px', color:'#64748b' }}>Response target:</span>
                <span style={{ fontSize:'13px', fontWeight:700, color:'#94a3b8' }}>{slaTarget} min</span>
                <span style={{ fontSize:'9px', color:'#475569' }}>(supervisor only)</span>
              </div>
            )}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
            {[
              { label:'Avg MTTA',       value: slaMetrics.avgMTTA !== null ? `${slaMetrics.avgMTTA} min` : '—',  sub: slaMetrics.avgMTTA !== null ? 'Mean time to acknowledge' : 'No acknowledged alarms yet',  color: slaMetrics.avgMTTA !== null ? '#3b82f6' : '#475569', icon:'⏱' },
              { label:'Avg MTTR',       value: slaMetrics.avgMTTR !== null ? `${slaMetrics.avgMTTR} min` : '—',  sub: slaMetrics.avgMTTR !== null ? 'Mean time to resolve' : 'No resolved alarms yet',      color: slaMetrics.avgMTTR !== null ? '#a855f7' : '#475569', icon:'🔧' },
              { label:'SLA Compliance', value:`${slaMetrics.slaTotal > 0 ? Math.round((slaMetrics.slaCompliant/slaMetrics.slaTotal)*100) : 100}%`, sub:`Target: respond <${slaMetrics.slaTarget} min`, color:'#22c55e', icon:'✓' },
              { label:'Resolved Today', value:String(slaMetrics.acked),     sub:'Acknowledged or closed',    color:'#f59e0b', icon:'📋' },
            ].map(s => (
              <div key={s.label} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
                  <div style={{ fontSize:'11px', color:'#64748b' }}>{s.label}</div>
                  <div style={{ fontSize:'18px' }}>{s.icon}</div>
                </div>
                <div style={{ fontSize:'28px', fontWeight:700, color:s.color, marginBottom:'4px' }}>{s.value}</div>
                <div style={{ fontSize:'10px', color:'#475569' }}>{s.sub}</div>
              </div>
            ))}
          </div>
          {/* Per-severity breakdown */}
          <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0', marginBottom:'14px' }}>Response Performance by Severity</div>
            {[
              { sev:'critical', label:'Critical', target:2, avg:1.8, count: alarms.filter(a=>a.severity==='critical').length },
              { sev:'high',     label:'High',     target:5, avg:4.2, count: alarms.filter(a=>a.severity==='high').length },
              { sev:'medium',   label:'Medium',   target:15, avg:8.6, count: alarms.filter(a=>a.severity==='medium').length },
              { sev:'low',      label:'Low',       target:30, avg:18.4, count: alarms.filter(a=>a.severity==='low').length },
            ].map(row => {
              const pct = Math.min(100, Math.round((row.avg / row.target) * 100));
              const onTarget = row.avg <= row.target;
              const color = onTarget ? '#22c55e' : '#ef4444';
              return (
                <div key={row.sev} style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'10px' }}>
                  <span style={{ width:'9px', height:'9px', borderRadius:'50%', background:SEV_COLOR[row.sev], flexShrink:0 }} />
                  <span style={{ width:'70px', fontSize:'12px', color:'#94a3b8', fontWeight:600 }}>{row.label}</span>
                  <span style={{ width:'50px', fontSize:'11px', color:'#64748b', textAlign:'right' }}>{row.count} alarms</span>
                  <div style={{ flex:1, height:'6px', background:'rgba(255,255,255,0.06)', borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:'3px', transition:'width 0.3s' }} />
                  </div>
                  <span style={{ width:'70px', fontSize:'11px', color:color, fontWeight:700, textAlign:'right' }}>{row.avg} min avg</span>
                  <span style={{ width:'80px', fontSize:'10px', color:'#475569', textAlign:'right' }}>Target: {row.target} min</span>
                  <span style={{ fontSize:'9px', fontWeight:700, padding:'1px 7px', borderRadius:'6px', background:onTarget?'rgba(34,197,94,0.12)':'rgba(239,68,68,0.12)', color }}>{onTarget?'ON TARGET':'BREACH'}</span>
                </div>
              );
            })}
          </div>
          {/* Hourly alarm volume chart */}
          <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0', marginBottom:'14px' }}>Alarm Volume — Last 12 Hours</div>
            <div style={{ display:'flex', alignItems:'flex-end', gap:'4px', height:'80px' }}>
              {[2,0,1,0,0,3,5,8,12,7,4,2].map((v, i) => {
                const max = 12;
                const pct = Math.round((v / max) * 100);
                const hour = (new Date().getHours() - 11 + i + 24) % 24;
                return (
                  <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'3px' }}>
                    <div style={{ fontSize:'9px', color:'#475569' }}>{v || ''}</div>
                    <div style={{ width:'100%', borderRadius:'3px 3px 0 0', height:`${Math.max(pct, 2)}%`, background: v > 8 ? '#ef4444' : v > 4 ? '#f59e0b' : '#3b82f6', opacity: 0.7, transition:'height 0.3s' }} />
                    <div style={{ fontSize:'8px', color:'#334155' }}>{String(hour).padStart(2,'0')}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Operator Performance */}
          <OperatorPerformancePanel />
        </div>
      )}


      {/* ── Alarm Status Tab ── */}
      {mainTab === 'alarmstatus' && (
        <div>
          <div style={{ background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'10px', padding:'10px 14px', marginBottom:'14px', fontSize:'11px', color:'#94a3b8' }}>
            <span style={{ color:'#f59e0b', fontWeight:600 }}>Alarm Status</span>
            {' '}- Maintenance windows silence selected alarm types for a defined period. Active suppressions are shown first.
            {' '}<span style={{ color:'#f59e0b' }}>{suppressions.filter(s=>s.active).length} active</span> · {suppressions.filter(s=>!s.active).length} expired
          </div>
          <div style={{ display:'grid', gap:'10px' }}>
            {suppressions.map(sw => (
              <div key={sw.id} style={{
                background:'rgba(255,255,255,0.03)', borderRadius:'12px', padding:'16px',
                border:`1px solid ${sw.active?'rgba(245,158,11,0.2)':'rgba(255,255,255,0.06)'}`,
                opacity: sw.active ? 1 : 0.55,
              }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:'14px', flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:'200px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'5px' }}>
                      <span style={{ fontSize:'13px', fontWeight:700, color:'#e2e8f0' }}>{sw.id}</span>
                      <span style={{ fontSize:'9px', padding:'2px 8px', borderRadius:'7px', fontWeight:700,
                        background: sw.active?'rgba(245,158,11,0.12)':'rgba(100,116,139,0.1)',
                        color: sw.active?'#f59e0b':'#64748b' }}>{sw.active?'ACTIVE':'EXPIRED'}</span>
                    </div>
                    <div style={{ fontSize:'12px', color:'#94a3b8', marginBottom:'3px' }}>{sw.site} — {sw.location}</div>
                    <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'5px' }}>{sw.reason}</div>
                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                      {sw.alarmTypes.map(at => (
                        <span key={at} style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'6px', background:'rgba(59,130,246,0.1)', color:'#3b82f6' }}>{at}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:'10px', color:'#64748b', marginBottom:'3px' }}>Window: <span style={{ color:'#94a3b8' }}>{sw.start} – {sw.end}</span></div>
                    <div style={{ fontSize:'10px', color:'#475569', marginBottom:'8px' }}>By: {sw.createdBy}</div>
                    {sw.active && (
                      <button
                        onClick={async () => {
                          const ruleId = (sw as any)._ruleId || sw.id;
                          const tok = localStorage.getItem('lemai_token') || '';
                          try {
                            await fetch(`/api/armorip/suppression/${ruleId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok}` } });
                          } catch { /* silent */ }
                          setSuppressions(prev => prev.filter(s => s.id !== sw.id));
                          toast('Suppression window removed', 'success');
                        }}
                        style={{ padding:'4px 12px', borderRadius:'7px', border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:'10px', fontWeight:600, cursor:'pointer' }}
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowSuppressionModal(true)}
            style={{ marginTop:'12px', width:'100%', padding:'10px', borderRadius:'10px', border:'1px dashed rgba(59,130,246,0.3)', background:'transparent', color:'#3b82f6', fontSize:'12px', fontWeight:600, cursor:'pointer' }}
          >
            + Add Maintenance Window
          </button>

          {showSuppressionModal && (
            <SuppressionCreateModal
              onClose={() => setShowSuppressionModal(false)}
              onCreated={(rule) => {
                setSuppressions(prev => [...prev, {
                  id: rule.id, site: rule.site || 'All Sites', location: rule.location || 'All Locations',
                  reason: rule.reason, start: new Date().toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit',hour12:false}),
                  end: new Date(rule.expiresAt * 1000).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit',hour12:false}),
                  createdBy: rule.createdBy, active: true,
                  alarmTypes: rule.alarmType ? [rule.alarmType] : ['All Types'],
                  _ruleId: rule.id,
                } as any]);
                setShowSuppressionModal(false);
                toast('Suppression window created', 'success');
              }}
            />
          )}
        </div>
      )}

      {/* ── Audit Trail Tab ──────────────────────────────────────────────── */}
      {mainTab === 'audit' && (() => {
        const filtered = auditEntries.filter(e => {
          if (auditCategory !== 'all' && e.category !== auditCategory) return false;
          if (auditSearch) {
            const q = auditSearch.toLowerCase();
            return (e.action+e.operator+(e.target||'')+(e.detail||'')).toLowerCase().includes(q);
          }
          return true;
        });
        return (
          <div>
            {/* Toolbar */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
              <select value={auditCategory} onChange={e=>setAuditCategory(e.target.value)}
                style={{ padding:'6px 10px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', color:'#e2e8f0', fontSize:'12px', cursor:'pointer' }}>
                <option value="all">All Categories</option>
                {(['alarm','access','user','config','report','job','incident','asset','system'] as const).map(c=>(
                  <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>
                ))}
              </select>
              <input value={auditSearch} onChange={e=>setAuditSearch(e.target.value)} placeholder="Search operator, action, target…"
                style={{ flex:1, minWidth:'160px', padding:'6px 12px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', color:'#e2e8f0', fontSize:'12px', outline:'none' }} />
              <button onClick={()=>exportCSV(`audit-trail-${csvDateStamp()}`,
                ['Timestamp','Category','Action','Operator','Site','Target','Detail','Severity'],
                filtered.map(e=>[new Date(e.ts).toISOString(),e.category,e.action,e.operator,e.site||'',e.target||'',e.detail||'',e.severity||'info'])
              )} style={{ padding:'6px 14px', background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'8px', color:'#22c55e', fontSize:'12px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                ↓ Export CSV
              </button>
            </div>

            {/* Summary pills */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
              {[
                { label:'Total', value:auditEntries.length, color:'#64748b' },
                { label:'Critical', value:auditEntries.filter(e=>e.severity==='critical').length, color:'#ef4444' },
                { label:'Warnings', value:auditEntries.filter(e=>e.severity==='warning').length, color:'#f59e0b' },
                { label:'Today', value:auditEntries.filter(e=>new Date(e.ts).toDateString()===new Date().toDateString()).length, color:'#3b82f6' },
              ].map(s=>(
                <div key={s.label} style={{ padding:'5px 12px', borderRadius:'8px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', fontSize:'12px' }}>
                  <span style={{ color:s.color, fontWeight:700 }}>{s.value}</span>
                  <span style={{ color:'#64748b', marginLeft:'5px' }}>{s.label}</span>
                </div>
              ))}
              <div style={{ padding:'5px 12px', borderRadius:'8px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', fontSize:'12px' }}>
                <span style={{ color:'#94a3b8', fontWeight:700 }}>{filtered.length}</span>
                <span style={{ color:'#64748b', marginLeft:'5px' }}>Showing</span>
              </div>
            </div>

            {/* Table */}
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'140px 80px 1fr 140px 100px 70px', padding:'8px 14px', borderBottom:'1px solid rgba(255,255,255,0.06)', fontSize:'10px', fontWeight:700, color:'#334155', letterSpacing:'0.5px', textTransform:'uppercase' }}>
                <span>Time</span><span>Category</span><span>Action / Target</span><span>Operator</span><span>Site</span><span>Severity</span>
              </div>
              <div style={{ maxHeight:'520px', overflowY:'auto' }}>
                {filtered.slice(0,200).map(e => (
                  <div key={e.id} style={{ display:'grid', gridTemplateColumns:'140px 80px 1fr 140px 100px 70px', padding:'9px 14px', borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'start' }}
                    onMouseEnter={el=>{ (el.currentTarget as HTMLElement).style.background='rgba(255,255,255,0.02)'; }}
                    onMouseLeave={el=>{ (el.currentTarget as HTMLElement).style.background='transparent'; }}>
                    <span style={{ fontSize:'11px', color:'#64748b', fontFamily:'monospace' }}>{fmtAuditTs(e.ts)}</span>
                    <span>
                      <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'6px', fontWeight:700,
                        background:`${AUDIT_CATEGORY_COLOR[e.category]}18`,
                        color:AUDIT_CATEGORY_COLOR[e.category] }}>{e.category}</span>
                    </span>
                    <div>
                      <div style={{ fontSize:'12px', color:'#e2e8f0', fontWeight:500 }}>{e.action}</div>
                      {e.target && <div style={{ fontSize:'11px', color:'#64748b', marginTop:'1px' }}>{e.target}</div>}
                      {e.before && <div style={{ fontSize:'10px', color:'#475569', marginTop:'2px' }}>{e.before} → {e.after}</div>}
                    </div>
                    <span style={{ fontSize:'11px', color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.operator}</span>
                    <span style={{ fontSize:'11px', color:'#64748b' }}>{e.site || '—'}</span>
                    <span>
                      <span style={{ fontSize:'10px', padding:'2px 7px', borderRadius:'6px', fontWeight:700,
                        background:`${AUDIT_SEVERITY_COLOR[e.severity||'info']}18`,
                        color:AUDIT_SEVERITY_COLOR[e.severity||'info'] }}>{e.severity||'info'}</span>
                    </span>
                  </div>
                ))}
                {filtered.length === 0 && (
                  <div style={{ padding:'40px', textAlign:'center', color:'#334155', fontSize:'13px' }}>No audit entries match your filter</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── System Events Tab (PSIM aggregated feed) ── */}
      {mainTab === 'events' && (() => {
        // Combine: live ArmorIP alarms + Protege GX events + SEED_EVENTS baseline
        const now = new Date();
        const liveEvts: SysEvent[] = alarms.slice(0, 30).map((a, i) => ({
          id: 10000 + i,
          time: a.time ? String(a.time).slice(0, 8) : now.toTimeString().slice(0, 8),
          type: 'Alarm',
          source: 'ArmorIP',
          site: a.site || '—',
          message: `${a.type} — ${a.location || a.site || ''}`,
          severity: (a.severity === 'critical' ? 'error' : a.severity === 'high' ? 'error' : 'warning') as SysEvent['severity'],
        }));
        const protegeEvts: SysEvent[] = protegeEvents.slice(0, 20).map((e, i) => ({
          id: 20000 + i,
          time: e.time ? String(e.time).slice(0, 8) : '--:--:--',
          type: e.event?.toLowerCase().includes('access') ? 'Access' : 'Event',
          source: 'Protege GX',
          site: e.controller || e.site || '—',
          message: e.event || e.type || 'Protege GX event',
          severity: 'info' as SysEvent['severity'],
        }));
        const combined = [...liveEvts, ...protegeEvts, ...SEED_EVENTS]
          .sort((a, b) => b.id - a.id)
          .slice(0, 60);

        const SOURCES = ['all', 'ArmorIP', 'Protege GX', 'Milestone', 'System', 'Lemai'];
        const filtered = combined.filter(e => {
          if (evtFilter !== 'all' && e.source !== evtFilter) return false;
          if (evtSearch) return (e.message + e.site + e.source + e.type).toLowerCase().includes(evtSearch.toLowerCase());
          return true;
        });
        const sevColor: Record<string, string> = { error:'#ef4444', warning:'#f59e0b', info:'#3b82f6' };
        const srcBadge: Record<string, string> = { 'ArmorIP':'#ef4444', 'Protege GX':'#3b82f6', 'Milestone':'#a855f7', 'System':'#64748b', 'Lemai':'#22c55e' };
        return (
          <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' as const }}>
              <select value={evtFilter} onChange={e => setEvtFilter(e.target.value)}
                style={{ padding:'5px 10px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'#e2e8f0', fontSize:'11px', cursor:'pointer' }}>
                {SOURCES.map(s => <option key={s} value={s}>{s === 'all' ? 'All Sources' : s}</option>)}
              </select>
              <input value={evtSearch} onChange={e => setEvtSearch(e.target.value)} placeholder="Search events…"
                style={{ flex:1, minWidth:'140px', padding:'5px 10px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'#e2e8f0', fontSize:'11px', outline:'none' }} />
              <div style={{ display:'flex', gap:'8px', fontSize:'11px' }}>
                <span style={{ color:'#22c55e' }}>● ArmorIP: Live</span>
                <span style={{ color: protegeEvents.length > 0 ? '#3b82f6' : '#475569' }}>● Protege GX: {protegeEvents.length > 0 ? 'Live' : 'Seed'}</span>
              </div>
            </div>
            <div style={{ maxHeight:'520px', overflowY:'auto' }}>
              {filtered.map((e, i) => (
                <div key={`${e.id}-${i}`} style={{
                  display:'grid', gridTemplateColumns:'90px 80px 90px 1fr 100px',
                  padding:'9px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)',
                  alignItems:'center', gap:'8px',
                  background: e.severity === 'error' ? 'rgba(239,68,68,0.02)' : 'transparent',
                }}
                  onMouseEnter={el => { (el.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
                  onMouseLeave={el => { (el.currentTarget as HTMLElement).style.background = e.severity === 'error' ? 'rgba(239,68,68,0.02)' : 'transparent'; }}>
                  <span style={{ fontFamily:'monospace', fontSize:'11px', color:'#475569' }}>{e.time}</span>
                  <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'10px', background:`${sevColor[e.severity] || '#64748b'}22`, color:sevColor[e.severity] || '#64748b', textAlign:'center' }}>
                    {e.type}
                  </span>
                  <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'10px', background:`${srcBadge[e.source] || '#64748b'}18`, color:srcBadge[e.source] || '#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {e.source}
                  </span>
                  <span style={{ fontSize:'12px', color: e.severity === 'error' ? '#fca5a5' : e.severity === 'warning' ? '#fcd34d' : '#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {e.message}
                  </span>
                  <span style={{ fontSize:'10px', color:'#475569', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.site}</span>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding:'40px', textAlign:'center', color:'#334155', fontSize:'13px' }}>No events match filter</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Lockdown Controller Modal ── */}
      {showLockdownModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }} onClick={()=>{ setShowLockdownModal(false); setLockdownSearch(''); }}>
          <div style={{ background:'#0d1321', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'16px', width:'480px', maxWidth:'95vw', maxHeight:'80vh', overflow:'hidden', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:700, color:'#ef4444' }}>Emergency Lockdown</div>
                  <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>Select a controller (site) to lock all associated doors</div>
                </div>
                <button onClick={()=>{ setShowLockdownModal(false); setLockdownSearch(''); }} style={{ background:'none', border:'none', color:'#64748b', fontSize:'18px', cursor:'pointer', lineHeight:1 }}>x</button>
              </div>
              <input
                autoFocus
                value={lockdownSearch}
                onChange={e=>setLockdownSearch(e.target.value)}
                placeholder="Search sites..."
                style={{ width:'100%', boxSizing:'border-box', padding:'7px 12px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', color:'white', fontSize:'12px', outline:'none' }}
              />
            </div>
            <div style={{ overflowY:'auto', padding:'12px 16px', display:'flex', flexDirection:'column', gap:'8px' }}>
              {(isProtegeOrg ? protegeControllers : SITES.map((s,i)=>({id:String(i+1),name:s,online:true})))
                .filter((ctrl:any) => !lockdownSearch || ctrl.name.toLowerCase().includes(lockdownSearch.toLowerCase()))
                .map((ctrl:any) => (
                <div key={ctrl.id} onClick={()=>handleLockdownController(ctrl)}
                  style={{ padding:'14px 16px', borderRadius:'10px', cursor:'pointer', border:'1px solid rgba(239,68,68,0.2)', background:'rgba(239,68,68,0.05)', transition:'all 0.15s' }}
                  onMouseOver={e=>(e.currentTarget as HTMLElement).style.background='rgba(239,68,68,0.12)'}
                  onMouseOut={e=>(e.currentTarget as HTMLElement).style.background='rgba(239,68,68,0.05)'}
                >
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:ctrl.online!==false?'#22c55e':'#94a3b8', flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0' }}>{ctrl.name}</div>
                      <div style={{ fontSize:'10px', color:'#64748b', marginTop:'2px' }}>Controller ID: {ctrl.id}</div>
                    </div>
                    <div style={{ fontSize:'11px', fontWeight:700, color:'#ef4444' }}>LOCK ALL DOORS</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', fontSize:'10px', color:'#475569' }}>
              Warning: This action is logged and will immediately lock all Protege GX doors at the selected site.
            </div>
          </div>
        </div>
      )}

      {/* ── Jobs Popup ── */}
      {showJobsPopup && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }} onClick={()=>setShowJobsPopup(false)}>
          <div style={{ background:'#0d1321', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'16px', width:'680px', maxWidth:'95vw', maxHeight:'80vh', overflow:'hidden', display:'flex', flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Active Jobs</div>
                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>{liveJobCount !== null ? (liveJobCount.open + liveJobCount.inProgress) : '--'} open jobs requiring attention</div>
              </div>
              <button onClick={()=>setShowJobsPopup(false)} style={{ background:'none', border:'none', color:'#64748b', fontSize:'18px', cursor:'pointer', lineHeight:1 }}>x</button>
            </div>
            <div style={{ overflowY:'auto', flex:1 }}>
              {openJobs.map((job:any) => {
                const PCLR: Record<string,string> = { Critical:'#ef4444', High:'#f59e0b', Medium:'#3b82f6', Low:'#64748b' };
                const SCLR: Record<string,string> = { in_progress:'#f59e0b', pending_review:'#3b82f6', assigned:'#a855f7', new:'#64748b' };
                return (
                  <div key={job.id} style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', gap:'14px', alignItems:'flex-start' }}>
                    <div style={{ padding:'2px 8px', borderRadius:'8px', fontSize:'9px', fontWeight:700, background:`${PCLR[job.priority]||'#64748b'}18`, color:PCLR[job.priority]||'#64748b', flexShrink:0, marginTop:'2px' }}>{job.priority}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                        <span style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0' }}>{job.type}</span>
                        <span style={{ fontSize:'9px', color:'#475569', fontFamily:'monospace' }}>{job.number}</span>
                      </div>
                      <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'3px' }}>{job.site} - {job.location}</div>
                      <div style={{ fontSize:'10px', color:'#475569' }}>{job.description}</div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ padding:'2px 8px', borderRadius:'7px', fontSize:'9px', fontWeight:700, background:`${SCLR[job.status]||'#64748b'}18`, color:SCLR[job.status]||'#64748b', marginBottom:'4px' }}>
                        {(job.status||'').replace('_',' ')}
                      </div>
                      <div style={{ fontSize:'9px', color:'#475569' }}>{job.assignedTo||'Unassigned'}</div>
                    </div>
                  </div>
                );
              })}
              {openJobs.length === 0 && (
                <div style={{ padding:'40px', textAlign:'center', color:'#444', fontSize:'10px' }}>No active jobs</div>
              )}
            </div>
            <div style={{ padding:'12px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end' }}>
              <a href="/jobs" style={{ padding:'6px 18px', borderRadius:'8px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontSize:'11px', fontWeight:700, textDecoration:'none' }}>Open Jobs Module</a>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

// ── Suppression Create Modal ──────────────────────────────────────────────────
function SuppressionCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (rule: any) => void }) {
  const [site, setSite] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [alarmType, setAlarmType] = React.useState('');
  const [reason, setReason] = React.useState('Scheduled maintenance');
  const [duration, setDuration] = React.useState(60);
  const [saving, setSaving] = React.useState(false);
  const { toast } = useToast();

  const inp = { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'9px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box' as const };
  const lbl = { display:'block' as const, fontSize:'10px', fontWeight:600 as const, color:'#64748b', marginBottom:'5px', textTransform:'uppercase' as const, letterSpacing:'0.5px' as const };

  async function create() {
    setSaving(true);
    try {
      const tok = localStorage.getItem('lemai_token') || '';
      const res = await fetch('/api/armorip/suppression', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ site, location, alarmType, reason, durationMinutes: duration }),
      });
      const data = await res.json();
      if (res.ok) onCreated(data.rule);
      else toast(data.error || 'Create failed', 'error');
    } catch { toast('Create failed', 'error'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:400, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'420px', maxWidth:'95vw', background:'#0d1321', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'14px', overflow:'hidden' }}>
        <div style={{ height:'2px', background:'linear-gradient(90deg,transparent,#f59e0b 30%,#ef4444 60%,transparent)' }} />
        <div style={{ padding:'24px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'18px' }}>
            <div style={{ fontSize:'15px', fontWeight:700, color:'#f1f5f9' }}>New Maintenance Window</div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'7px', color:'#64748b', width:'28px', height:'28px', cursor:'pointer', fontSize:'13px' }}>✕</button>
          </div>
          <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'16px', padding:'8px 12px', background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'8px' }}>
            Leave Site, Location, or Alarm Type blank to act as a wildcard (matches all).
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div><label style={lbl}>Site (or blank = all)</label><input value={site} onChange={e=>setSite(e.target.value)} placeholder="e.g. Town Hall" style={inp} /></div>
              <div><label style={lbl}>Location (or blank = all)</label><input value={location} onChange={e=>setLocation(e.target.value)} placeholder="e.g. Level 2" style={inp} /></div>
            </div>
            <div><label style={lbl}>Alarm Type (or blank = all types)</label><input value={alarmType} onChange={e=>setAlarmType(e.target.value)} placeholder="e.g. Motion Detected" style={inp} /></div>
            <div><label style={lbl}>Reason</label><input value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. HVAC maintenance" style={inp} /></div>
            <div>
              <label style={lbl}>Duration</label>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <input type="number" min={1} max={1440} value={duration} onChange={e=>setDuration(Math.max(1,parseInt(e.target.value)||60))} style={{ ...inp, width:'80px' }} />
                <span style={{ fontSize:'12px', color:'#94a3b8' }}>minutes</span>
                <div style={{ display:'flex', gap:'4px' }}>
                  {[30,60,120,240,480].map(d => (
                    <button key={d} onClick={() => setDuration(d)} style={{ padding:'3px 8px', borderRadius:'5px', border:`1px solid ${duration===d?'rgba(245,158,11,0.4)':'rgba(255,255,255,0.08)'}`, background:duration===d?'rgba(245,158,11,0.1)':'transparent', color:duration===d?'#f59e0b':'#64748b', fontSize:'10px', cursor:'pointer' }}>
                      {d >= 60 ? `${d/60}h` : `${d}m`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button onClick={onClose} style={{ flex:1, padding:'9px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#64748b', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={create} disabled={saving} style={{ flex:2, padding:'9px', borderRadius:'8px', border:'none', background:saving?'rgba(245,158,11,0.3)':'linear-gradient(135deg,#f59e0b,#d97706)', color:'black', fontSize:'12px', fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
              {saving ? 'Creating…' : 'Create Window'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Operator Performance Panel ────────────────────────────────────────────────
function OperatorPerformancePanel() {
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [days, setDays] = React.useState(30);

  async function load() {
    setLoading(true);
    try {
      const tok = localStorage.getItem('lemai_token') || '';
      const res = await fetch(`/api/armorip/performance?days=${days}`, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.ok) setData(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }

  React.useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCSV = () => {
    if (!data?.operators?.length) return;
    const rows = [['Operator','Total Acked','Avg Ack (min)','SLA Pass','SLA Fail','SLA %','Critical','High','Medium','Low']];
    for (const op of data.operators) {
      rows.push([op.operator, op.total_acked, op.avg_ack_min ?? '—', op.sla_pass, op.sla_fail, op.sla_pct != null ? `${op.sla_pct}%` : '—', op.by_severity?.critical??0, op.by_severity?.high??0, op.by_severity?.medium??0, op.by_severity?.low??0]);
    }
    const csv = rows.map(r => r.map(String).map(v => `"${v.toString().replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
    a.download = `operator-performance-${days}d.csv`; a.click();
  };

  return (
    <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'16px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
        <div style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0' }}>👤 Operator Response Performance</div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            style={{ padding:'4px 8px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'#e2e8f0', fontSize:'11px', cursor:'pointer' }}>
            {[7,14,30,60,90].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button onClick={downloadCSV} disabled={!data?.operators?.length} style={{ padding:'4px 10px', borderRadius:'6px', border:'1px solid rgba(34,197,94,0.3)', background:'rgba(34,197,94,0.08)', color:'#22c55e', fontSize:'10px', fontWeight:600, cursor:'pointer' }}>↓ CSV</button>
        </div>
      </div>

      {loading && <div style={{ padding:'20px', textAlign:'center', color:'#475569', fontSize:'12px' }}>Loading performance data…</div>}
      {!loading && (!data?.operators?.length) && (
        <div style={{ padding:'20px', textAlign:'center', color:'#334155', fontSize:'12px' }}>
          No acknowledged alarms in the last {days} days. Performance data will appear here once operators start acknowledging alarms.
        </div>
      )}
      {!loading && data?.operators?.length > 0 && (
        <div style={{ overflowX:'auto' as const }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'11px' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                {['Operator','Total Acked','Avg Ack','SLA Pass','SLA Fail','SLA %','Crit','High','Med','Low'].map(h => (
                  <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:'#64748b', fontWeight:600, whiteSpace:'nowrap' as const }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.operators.map((op: any) => {
                const slaColor = op.sla_pct == null ? '#64748b' : op.sla_pct >= 90 ? '#22c55e' : op.sla_pct >= 70 ? '#f59e0b' : '#ef4444';
                return (
                  <tr key={op.operator} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                    onMouseOver={e=>(e.currentTarget as HTMLElement).style.background='rgba(255,255,255,0.02)'}
                    onMouseOut={e=>(e.currentTarget as HTMLElement).style.background='transparent'}>
                    <td style={{ padding:'8px 10px', fontWeight:600, color:'#e2e8f0' }}>{op.operator}</td>
                    <td style={{ padding:'8px 10px', color:'#94a3b8' }}>{op.total_acked}</td>
                    <td style={{ padding:'8px 10px', color: op.avg_ack_min != null && op.avg_ack_min <= 5 ? '#22c55e' : '#f59e0b' }}>{op.avg_ack_min != null ? `${op.avg_ack_min}m` : '—'}</td>
                    <td style={{ padding:'8px 10px', color:'#22c55e' }}>{op.sla_pass}</td>
                    <td style={{ padding:'8px 10px', color: op.sla_fail > 0 ? '#ef4444' : '#64748b' }}>{op.sla_fail}</td>
                    <td style={{ padding:'8px 10px', fontWeight:700, color: slaColor }}>{op.sla_pct != null ? `${op.sla_pct}%` : '—'}</td>
                    <td style={{ padding:'8px 10px', color:'#fca5a5' }}>{op.by_severity?.critical ?? 0}</td>
                    <td style={{ padding:'8px 10px', color:'#fbbf24' }}>{op.by_severity?.high ?? 0}</td>
                    <td style={{ padding:'8px 10px', color:'#60a5fa' }}>{op.by_severity?.medium ?? 0}</td>
                    <td style={{ padding:'8px 10px', color:'#94a3b8' }}>{op.by_severity?.low ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ marginTop:'10px', fontSize:'10px', color:'#334155' }}>
            Based on {data.total_alarms_in_period} alarms over last {data.days} days · SLA target: {data.sla_target_min} min
          </div>
        </div>
      )}
    </div>
  );
}
